import type {
  ApprovalDecision,
  AttemptHeader,
  AttemptStatus,
  StructuredDocument,
  ResultPath,
  StructuredResultView,
  BillableServiceView,
  CompletedMaintenanceView,
  CorrectionStatus,
  CorrectiveActionView,
  EligibleResultView,
  EquipmentCalibrationView,
  EquipmentUsageView,
  EquipmentView,
  GeneralResultContent,
  ImportKind,
  ImportReport,
  InvoiceDetailView,
  InvoiceView,
  MaintenanceRecordView,
  ManagementDashboardView,
  NotificationListView,
  NotificationPreferencesView,
  NotificationPreferenceView,
  NotificationView,
  PaginatedResponse,
  PaymentDetailView,
  PaymentView,
  PortalAccountDetailView,
  PortalAccountView,
  QualityRecordDetailView,
  QualityRecordView,
  RevisionAttachmentEntry,
  RevisionComparison,
  RevisionEquipmentEntry,
  ReportDeliveryStatus,
  ReportDetailView,
  ReportLanguage,
  ReportRegisterItemView,
  ReportStatus,
  ReportTemplateResolutionView,
  ReportTemplateStatus,
  ReportTemplateView,
  ReviewDecision,
  StatementView,
} from '@lims/shared-types';
import {
  ALL_REPORT_LANGUAGES,
  ALL_REPORT_VERSION_STATUSES,
  ERROR_CODES,
  PUBLIC_VERIFICATION_FIELDS,
  isReportVerificationToken,
  type ApiErrorBody,
  type PublicVerificationView,
  type ReportVersionStatus,
} from '@lims/shared-types';
import type {
  CreatePortalAccountInput,
  CreateReportTemplateInput,
  OpenReportCorrectionInput,
  PrepareReportInput,
  RecordReportDeliveryInput,
  UpdatePortalAccountInput,
  UpdateReportDraftInput,
  UpdateReportTemplateInput,
  SaveStructuredResultInput,
} from '@lims/shared-validation';
import type { Role } from '@lims/permissions';
import {
  ApiError,
  apiDownload,
  apiRequest,
  apiUrl,
  resolveApiPath,
  session,
} from './client.js';

/**
 * Typed wrappers over the Phase 1 endpoints.
 *
 * One function per endpoint, so a change to a path or payload happens in one
 * place rather than being spread through components.
 */

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export interface TenantSummary {
  id?: string;
  slug: string;
  nameAr: string | null;
  nameEn: string | null;
  /**
   * The laboratory's IANA timezone. Sent with the signed-in user (login and
   * `/auth/me`), absent from the public branding.
   */
  timezone?: string;
}

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  preferredLocale: string;
  permissions: string[];
  tenant: TenantSummary | null;
}

export interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: CurrentUser;
}

export interface UserSummary {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  preferredLocale: string;
  isActive: boolean;
  deactivatedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface UserDetail extends UserSummary {
  rolePermissions: string[];
  grantedPermissions: string[];
  revokedPermissions: string[];
  effectivePermissions: string[];
}

export interface PermissionCatalogue {
  permissions: { key: string; category: string }[];
  roles: { key: Role; defaultPermissions: string[] }[];
  separateApprovalKeys: { review: string; approve: string };
}

/**
 * A stored file, as the API describes it.
 *
 * Note what is ABSENT: there is no storage key and no path. The interface
 * addresses a file by id and downloads it through an endpoint; where the bytes
 * physically live is not something a client is ever told.
 */
export interface StoredFile {
  id: string;
  logicalName: string;
  description: string | null;
  mimeType: string;
  byteSize: number;
  checksumSha256: string;
  version: number;
  ownerEntityType: string;
  ownerEntityId: string | null;
  retentionPolicy: string;
  retainUntil: string | null;
  uploadedBy: string | null;
  uploadedByName: string | null;
  createdAt: string;
  deletedAt: string | null;
  isPurged: boolean;
}

export interface SignedDownloadLink {
  url: string;
  expiresAt: string;
  ttlSeconds: number;
}

export interface StorageUsage {
  usedBytes: number;
  quotaBytes: number;
  fileCount: number;
  deletedFileCount: number;
  deletedBytes: number;
  maxUploadBytes: number;
  retentionDays: number | null;
  byEntityType: { entityType: string; fileCount: number; bytes: number }[];
}

export interface StorageOrphans {
  missingObjects: string[];
  orphanedObjectCount: number;
  scannedObjectCount: number;
}

export interface RetentionRunResult {
  expired: number;
  purged: number;
  bytesReclaimed: number;
  dryRun: boolean;
}

export interface TenantLogo {
  fileId: string;
  /** A path served by the API. Never a storage key and never a filesystem path. */
  url: string;
  logicalName: string;
  mimeType: string;
  byteSize: number;
}

export interface TenantSettings {
  tenant: TenantSummary;
  addressAr: string | null;
  addressEn: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  defaultLocale: string;
  timezone: string;
  currency: string;
  taxRatePercent: number;
  requirePaymentBeforeDelivery: boolean;
  allowPartialReports: boolean;
  showHijriDates: boolean;
  vatRegistrationNumber: string | null;
  commercialRegistrationNumber: string | null;
  /** Which client fields this laboratory insists on. Empty by default. */
  clientRequiredFields: string[];
  /** The laboratory's own quotation policy. The interface never restates the
   *  product default; it renders whatever the backend reports. */
  quotationValidityDays: number;
  /** General conditions, copied into each new quotation. */
  quotationTerms: BilingualPair;
  /**
   * What a quotation document prints about the laboratory (D199). Every one
   * starts empty — the product supplies no laboratory's wording or bank.
   */
  city: BilingualPair;
  countryCode: string | null;
  website: string | null;
  bank: {
    accountName: string | null;
    bankName: string | null;
    accountNumber: string | null;
    iban: string | null;
    swiftCode: string | null;
  };
  quotationIntroduction: BilingualPair;
  quotationPaymentTerms: BilingualPair;
  quotationFooterText: BilingualPair;
  logo: TenantLogo | null;
  /**
   * Phase 10 (D126). The laboratory's own footer wording, printed at the foot
   * of every report issued from now on. Both halves are null by default: the
   * product supplies no wording (CLAUDE.md section 6.1).
   */
  reportFooterText: BilingualPair;
  /**
   * Phase 10 (D111). Whether a holder of a report's verification link may
   * download the original from the public page. The backend's default for a
   * new laboratory is OFF; the interface never restates it.
   */
  verificationDownloadEnabled: boolean;
  fileRetentionDays: number | null;
  storageQuotaBytes: number | null;
  /**
   * Phase 11. Days from issuance to an invoice's due date when the draft
   * names none of its own. The backend default for a new laboratory is 30;
   * this page never restates it (CLAUDE.md section 6.1).
   */
  paymentTermsDays: number;
  /**
   * Phase 12 (D173). How many days ahead a calibration or maintenance date
   * raises a notification. Its default lives only in the backend.
   */
  equipmentNoticeDays: number;
  /**
   * Phase 11 (D146). Whether the client portal is switched on for this
   * laboratory at all. The backend's default for a new laboratory is OFF;
   * this page never restates it (CLAUDE.md section 6.1).
   */
  clientPortalEnabled: boolean;
  /**
   * The visibility applied when a contact is enabled for the portal without
   * naming one of its own (`reports` or `reports_and_invoices` — never
   * `none`). The backend's default is `reports`.
   */
  portalDefaultVisibility: string;
  /** The project scope a new portal account gets when creation names none
   *  of its own (`all_projects` or `selected_projects`). The backend's
   *  default is `all_projects`. */
  portalDefaultProjectScope: string;
  updatedAt: string;
}

export interface AuditEntry {
  id: string;
  actor: { userId: string; displayName: string | null; role: string | null } | null;
  action: string;
  entityType: string;
  entityId: string;
  before: unknown;
  after: unknown;
  reason: string | null;
  occurredAt: string;
  ipAddress: string | null;
  requestId: string | null;
}

// ---------------------------------------------------------------------------
// Authentication
// ---------------------------------------------------------------------------

export const authApi = {
  /** The laboratory name shown on the sign-in page, before anyone signs in. */
  branding: () =>
    apiRequest<{ tenant: TenantSummary | null }>('/auth/branding', {
      anonymous: true,
    }),

  async login(email: string, password: string): Promise<CurrentUser> {
    const result = await apiRequest<SessionResponse>('/auth/login', {
      method: 'POST',
      anonymous: true,
      body: { email, password },
    });
    session.store(result.accessToken, result.refreshToken);
    return result.user;
  },

  me: () => apiRequest<CurrentUser>('/auth/me'),

  updateOwnProfile: (input: {
    displayName?: string;
    preferredLocale?: 'ar' | 'en';
  }) => apiRequest<UserSummary>('/auth/me', { method: 'PATCH', body: input }),

  /**
   * Change your own password (D181). The current one is required; every other
   * session ends and this one stays open. A wrong current password is a 400
   * `CURRENT_PASSWORD_INCORRECT`, never a 401, so it cannot sign you out.
   */
  changeOwnPassword: (input: { currentPassword: string; newPassword: string }) =>
    apiRequest<{ otherSessionsRevoked: true }>('/auth/me/password', {
      method: 'POST',
      body: input,
    }),

  async logout(): Promise<void> {
    // The server revokes the session; clearing local storage alone would leave
    // a token that still worked.
    try {
      await apiRequest<void>('/auth/logout', { method: 'POST' });
    } finally {
      session.clear();
    }
  },

  requestPasswordReset: (email: string) =>
    apiRequest<{ status: string }>('/auth/password-reset', {
      method: 'POST',
      anonymous: true,
      body: { email },
    }),

  confirmPasswordReset: (token: string, password: string) =>
    apiRequest<void>('/auth/password-reset/confirm', {
      method: 'POST',
      anonymous: true,
      body: { token, password },
    }),
};

// ---------------------------------------------------------------------------
// Users and permissions
// ---------------------------------------------------------------------------

export const usersApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    includeInactive?: boolean;
  }) =>
    apiRequest<PaginatedResponse<UserSummary>>('/users', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        includeInactive: query.includeInactive === false ? 'false' : 'true',
      },
    }),

  get: (id: string) => apiRequest<UserDetail>(`/users/${id}`),

  /** The manager sets the first password (D179); nothing is emailed. */
  create: (input: {
    email: string;
    displayName: string;
    role: Role;
    password: string;
    preferredLanguage?: string;
    grantedPermissions?: string[];
    revokedPermissions?: string[];
  }) => apiRequest<UserDetail>('/users', { method: 'POST', body: input }),

  /**
   * Set a new password for a colleague (D180). Needs `users.update` AND
   * `permissions.manage`; ends every session they hold; sends nothing.
   */
  setPassword: (id: string, input: { password: string; reason?: string }) =>
    apiRequest<{ id: string; sessionsRevoked: true }>(`/users/${id}/password`, {
      method: 'POST',
      body: input,
    }),

  update: (
    id: string,
    input: {
      email?: string;
      displayName?: string;
      role?: Role;
      preferredLanguage?: string;
      isActive?: boolean;
      reason?: string;
    },
  ) => apiRequest<UserDetail>(`/users/${id}`, { method: 'PATCH', body: input }),

  setPermissions: (
    id: string,
    input: {
      grantedPermissions: string[];
      revokedPermissions: string[];
      reason?: string;
    },
  ) =>
    apiRequest<UserDetail>(`/users/${id}/permissions`, {
      method: 'PUT',
      body: input,
    }),
};

export const permissionsApi = {
  catalogue: () => apiRequest<PermissionCatalogue>('/permissions'),
};

// ---------------------------------------------------------------------------
// Settings and audit
// ---------------------------------------------------------------------------

export const settingsApi = {
  get: () => apiRequest<TenantSettings>('/settings'),
  update: (input: Record<string, unknown>) =>
    apiRequest<TenantSettings>('/settings', { method: 'PATCH', body: input }),
};

// ---------------------------------------------------------------------------
// Files and storage
// ---------------------------------------------------------------------------

export const filesApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    entityType?: string;
    entityId?: string;
    includeDeleted?: boolean;
  }) =>
    apiRequest<PaginatedResponse<StoredFile>>('/files', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.entityType ? { entityType: query.entityType } : {}),
        ...(query.entityId ? { entityId: query.entityId } : {}),
        includeDeleted: query.includeDeleted === true ? 'true' : 'false',
      },
    }),

  get: (id: string) => apiRequest<StoredFile>(`/files/${id}`),

  /**
   * Upload one file.
   *
   * The `Content-Type` is deliberately NOT set here — the browser sets it,
   * with the multipart boundary it chose. The filename travels in the
   * multipart part; the server treats it as metadata and generates its own
   * opaque storage key, so nothing typed into a file name reaches a path.
   */
  upload: (input: {
    file: File;
    entityType: string;
    entityId?: string;
    description?: string;
    retentionPolicy?: 'standard' | 'permanent';
  }) => {
    const form = new FormData();
    form.append('file', input.file, input.file.name);
    form.append('entityType', input.entityType);
    if (input.entityId) form.append('entityId', input.entityId);
    if (input.description) form.append('description', input.description);
    if (input.retentionPolicy) {
      form.append('retentionPolicy', input.retentionPolicy);
    }
    return apiRequest<StoredFile>('/files', { method: 'POST', formData: form });
  },

  /**
   * Ask for a short-lived signed link.
   *
   * Used where a browser cannot attach a bearer token: an image, an embed, or
   * a download the user opens in a new tab. The link is minted by the server
   * only after it has re-checked the permission and the laboratory.
   */
  link: async (id: string, disposition: 'attachment' | 'inline' = 'attachment') => {
    const result = await apiRequest<SignedDownloadLink>(`/files/${id}/link`, {
      method: 'POST',
      body: { disposition },
    });
    return { ...result, url: resolveApiPath(result.url) };
  },

  /** Soft-delete. The metadata row and the audit trail both survive. */
  remove: (id: string, reason?: string) =>
    apiRequest<StoredFile>(`/files/${id}`, {
      method: 'DELETE',
      body: reason ? { reason } : {},
    }),
};

export const storageApi = {
  usage: () => apiRequest<StorageUsage>('/storage/usage'),
  orphans: () => apiRequest<StorageOrphans>('/storage/orphans'),
  runRetention: (input: { dryRun: boolean; graceDays?: number }) =>
    apiRequest<RetentionRunResult>('/storage/retention/run', {
      method: 'POST',
      body: { dryRun: input.dryRun, graceDays: input.graceDays ?? 30 },
    }),
};

export const auditApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    action?: string;
    entityType?: string;
    /** One record's own history. The work-order file reads its trail with it. */
    entityId?: string;
    actorUserId?: string;
    from?: string;
    to?: string;
  }) =>
    apiRequest<PaginatedResponse<AuditEntry>>('/audit', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.action ? { action: query.action } : {}),
        ...(query.entityType ? { entityType: query.entityType } : {}),
        ...(query.entityId ? { entityId: query.entityId } : {}),
        ...(query.actorUserId ? { actorUserId: query.actorUserId } : {}),
        ...(query.from ? { from: query.from } : {}),
        ...(query.to ? { to: query.to } : {}),
      },
    }),

  actions: () => apiRequest<{ actions: string[] }>('/audit/actions'),
};

// ---------------------------------------------------------------------------
// Clients, contacts, projects and locations (Phase 4)
// ---------------------------------------------------------------------------

/** Bilingual content, as the API returns it. Never auto-translated. */
export interface BilingualPair {
  ar: string | null;
  en: string | null;
}

export interface ClientSummary {
  id: string;
  clientNumber: string;
  /** ONE name, as typed (D183). */
  name: string;
  clientType: string;
  contactEmail: string | null;
  contactPhone: string | null;
  city: string | null;
  status: string;
  suspendedAt: string | null;
  createdAt: string;
}

export interface ClientDetail extends ClientSummary {
  vatRegistrationNumber: string | null;
  commercialRegistrationNumber: string | null;
  addressLine: string | null;
  countryCode: string | null;
  notes: string | null;
  suspensionReason: string | null;
  suspendedBy: string | null;
  updatedAt: string;
  /** Computed by the backend. The interface never restates the rule. */
  acceptsNewDealings: boolean;
}

export interface DuplicateMatchReason {
  field: string;
  confidence: string;
  score?: number;
  language?: 'ar' | 'en';
}

export interface DuplicateMatch {
  clientId: string;
  clientNumber: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  status: string;
  confidence: string;
  /** True for a match that must be acknowledged before a save succeeds. */
  requiresAcknowledgement: boolean;
  reasons: DuplicateMatchReason[];
}

export interface DuplicateSearchResult {
  matches: DuplicateMatch[];
  /** Proof the search happened. Presented back when the client is created. */
  searchToken: string;
  expiresAt: string;
  /** True when more candidates existed than were scored. Shown, not hidden. */
  candidatesTruncated: boolean;
}

/**
 * A section of the client file.
 *
 * `count` is `null` whenever `state` is `not_implemented`. It is NEVER zero
 * for a module that does not exist: zero would assert that this client has no
 * invoices, which nothing has established.
 */
export interface ClientFileSection {
  state: string;
  count: number | null;
  plannedPhase?: number;
}

export interface ClientFile {
  client: ClientDetail;
  sections: {
    projects: ClientFileSection;
    workOrders: ClientFileSection;
    reports: ClientFileSection;
    invoices: ClientFileSection;
  };
}

export interface ClientContact {
  id: string;
  clientId: string;
  name: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  isPrimary: boolean;
  /**
   * Whether this person MAY have a portal account. It grants nothing by
   * itself: the account is a separate record (`portalAccountsApi`), created
   * only for a contact enabled here, with its own email and scope.
   */
  portalEnabled: boolean;
  portalVisibility: string;
  notes: string | null;
  isActive: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectSummary {
  id: string;
  clientId: string;
  clientNumber: string;
  clientName: string;
  reference: string | null;
  name: string;
  status: string;
  startDate: string | null;
  expectedEndDate: string | null;
  createdAt: string;
}

export interface ProjectDetail extends ProjectSummary {
  description: string | null;
  contractorName: string | null;
  consultantName: string | null;
  ownerName: string | null;
  statusReason: string | null;
  notes: string | null;
  updatedAt: string;
  acceptsNewWork: boolean;
}

export interface ProjectLocation {
  id: string;
  projectId: string;
  name: string;
  description: string | null;
  addressLine: string | null;
  city: string | null;
  /** Decimal strings, not floats: the stored precision survives the trip. */
  latitude: string | null;
  longitude: string | null;
  notes: string | null;
  isActive: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NumberingRule {
  recordType: string;
  format: string;
  resetPolicy: string;
  /** Illustrations. Nothing here is allocated or reserved. */
  preview: string[];
  issuedThisPeriod: number;
  updatedAt: string | null;
}

export const clientsApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    includeSuspended?: boolean;
  }) =>
    apiRequest<PaginatedResponse<ClientSummary>>('/clients', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        ...(query.status ? { status: query.status } : {}),
        includeSuspended: query.includeSuspended === false ? 'false' : 'true',
      },
    }),

  /**
   * Search before creating.
   *
   * `POST`, not `GET`: a prospective client's identifying details do not
   * belong in a URL, a browser history or a proxy log — and this mints the
   * token the create endpoint requires.
   */
  searchDuplicates: (input: {
    name?: string;
    contactEmail?: string;
    contactPhone?: string;
    vatRegistrationNumber?: string;
    commercialRegistrationNumber?: string;
  }) =>
    apiRequest<DuplicateSearchResult>('/clients/duplicate-search', {
      method: 'POST',
      body: input,
    }),

  get: (id: string) => apiRequest<ClientDetail>(`/clients/${id}`),

  file: (id: string) => apiRequest<ClientFile>(`/clients/${id}/file`),

  create: (input: Record<string, unknown>) =>
    apiRequest<ClientDetail>('/clients', { method: 'POST', body: input }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<ClientDetail>(`/clients/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  /** Stops NEW dealings. Changes nothing already recorded. */
  suspend: (id: string, reason: string) =>
    apiRequest<ClientDetail>(`/clients/${id}/suspend`, {
      method: 'POST',
      body: { reason },
    }),

  reinstate: (id: string, reason?: string) =>
    apiRequest<ClientDetail>(`/clients/${id}/reinstate`, {
      method: 'POST',
      body: reason ? { reason } : {},
    }),

  listContacts: (id: string, includeInactive = true) =>
    apiRequest<ClientContact[]>(`/clients/${id}/contacts`, {
      query: { includeInactive: includeInactive ? 'true' : 'false' },
    }),

  createContact: (id: string, input: Record<string, unknown>) =>
    apiRequest<ClientContact>(`/clients/${id}/contacts`, {
      method: 'POST',
      body: input,
    }),

  updateContact: (
    id: string,
    contactId: string,
    input: Record<string, unknown>,
  ) =>
    apiRequest<ClientContact>(`/clients/${id}/contacts/${contactId}`, {
      method: 'PATCH',
      body: input,
    }),
};

// ---------------------------------------------------------------------------
// Client portal administration — Phase 11 (staff side; `portal_accounts.manage`)
//
// The account is a SEPARATE record from the contact. Creating one asks the
// identity provider for an invitation but does not confirm it arrived
// (`invitationRequested`, never "sent" — CLAUDE.md section 8). Deactivating
// one revokes every live portal session and touches no work order, report,
// invoice, contact or client (contract P's criterion 10).
// ---------------------------------------------------------------------------

export const portalAccountsApi = {
  /** Bare array, like `clientsApi.listContacts`. */
  list: (clientId: string) =>
    apiRequest<PortalAccountView[]>(`/clients/${clientId}/portal-accounts`),

  create: (clientId: string, contactId: string, input: CreatePortalAccountInput) =>
    apiRequest<PortalAccountView>(
      `/clients/${clientId}/contacts/${contactId}/portal-account`,
      { method: 'POST', body: input },
    ),

  /** Adds the last 50 access events, newest first. */
  get: (id: string) => apiRequest<PortalAccountDetailView>(`/portal-accounts/${id}`),

  update: (id: string, input: UpdatePortalAccountInput) =>
    apiRequest<PortalAccountView>(`/portal-accounts/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  deactivate: (id: string, reason: string) =>
    apiRequest<PortalAccountView>(`/portal-accounts/${id}/deactivate`, {
      method: 'POST',
      body: { reason },
    }),

  reactivate: (id: string, reason: string) =>
    apiRequest<PortalAccountView>(`/portal-accounts/${id}/reactivate`, {
      method: 'POST',
      body: { reason },
    }),

  /** Asks the identity provider to send an invitation again. Asking is not
   *  delivery: nothing confirms the message arrived. */
  invite: (id: string) =>
    apiRequest<{ invitationRequested: boolean }>(`/portal-accounts/${id}/invite`, {
      method: 'POST',
      body: {},
    }),
};

export const projectsApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    clientId?: string;
    status?: string;
  }) =>
    apiRequest<PaginatedResponse<ProjectSummary>>('/projects', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        ...(query.clientId ? { clientId: query.clientId } : {}),
        ...(query.status ? { status: query.status } : {}),
      },
    }),

  get: (id: string) => apiRequest<ProjectDetail>(`/projects/${id}`),

  create: (input: Record<string, unknown>) =>
    apiRequest<ProjectDetail>('/projects', { method: 'POST', body: input }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<ProjectDetail>(`/projects/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  listLocations: (id: string, includeInactive = true) =>
    apiRequest<ProjectLocation[]>(`/projects/${id}/locations`, {
      query: { includeInactive: includeInactive ? 'true' : 'false' },
    }),

  createLocation: (id: string, input: Record<string, unknown>) =>
    apiRequest<ProjectLocation>(`/projects/${id}/locations`, {
      method: 'POST',
      body: input,
    }),

  updateLocation: (
    id: string,
    locationId: string,
    input: Record<string, unknown>,
  ) =>
    apiRequest<ProjectLocation>(`/projects/${id}/locations/${locationId}`, {
      method: 'PATCH',
      body: input,
    }),
};

export const numberingApi = {
  list: () => apiRequest<{ rules: NumberingRule[] }>('/settings/numbering'),

  /** Illustrates a format. Allocates nothing and reserves nothing. */
  preview: (input: { format: string; resetPolicy: string }) =>
    apiRequest<{ preview: string[] }>('/settings/numbering/preview', {
      method: 'POST',
      body: input,
    }),

  update: (
    recordType: string,
    input: { format: string; resetPolicy: string },
  ) =>
    apiRequest<NumberingRule>(`/settings/numbering/${recordType}`, {
      method: 'PUT',
      body: input,
    }),
};

// ---------------------------------------------------------------------------
// Catalogue, quotations and pricing agreements (Phase 5)
// ---------------------------------------------------------------------------

/**
 * A catalogue entry, as the API describes it.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING A FIELD HERE.
 * ===========================================================================
 *
 * Note what is ABSENT, deliberately and for every test including the five
 * initial ones: input fields, readings, units of measurement, formulas,
 * calculation order, rounding rules, acceptance limits, charts, chart axes,
 * Excel cell mappings and specialised result or report layouts. None of them
 * exists in this product. `templateReadiness` is the honest name for the gap,
 * and `usesGeneralWorkflowOnly` is the backend saying so as a fact rather than
 * leaving this interface to work it out.
 */
export interface CatalogueItem {
  id: string;
  code: string | null;
  name: BilingualPair;
  category: { id: string; name: BilingualPair } | null;
  /**
   * A REFERENCE to the laboratory's own department register, shaped like
   * `category` above. Phase 5 held this as free text because the register did
   * not exist; Phase 6 built it, and migration 0005 moved every distinct text
   * a laboratory had typed into a row of its own.
   *
   * Null means no department has been stated. It does not mean "general".
   */
  /** The department's ONE name (D183). */
  department: { id: string; name: string } | null;
  /** A designation cited on documents. Nothing here parses or implements it. */
  referenceStandard: string | null;
  standardVersion: string | null;
  sampleType: string | null;
  /** Null when nobody has stated one. Not a duration of zero. */
  expectedDuration: { value: number; unit: string } | null;
  /** Null means UNPRICED. It never means free. */
  basePriceMinor: number | null;
  /** From the laboratory's settings. The interface never restates it. */
  currency: string;
  pricingUnit: string;
  /** Free text until the equipment register arrives in Phase 8. */
  requiredEquipment: BilingualPair[];
  reportLanguages: string[];
  templateReadiness: string;
  resultTemplateVersion: string | null;
  reportTemplateReference: string | null;
  /** The report template this test's reports use instead of the default (D254). */
  reportTemplateId: string | null;
  /** Computed by the backend. True for every entry in the product today. */
  usesGeneralWorkflowOnly: boolean;
  notes: string | null;
  isActive: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TestCategory {
  id: string;
  code: string | null;
  name: BilingualPair;
  description: BilingualPair;
  sortOrder: number;
  isActive: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PricingAgreementLine {
  id: string;
  catalogueItemId: string;
  itemCode: string | null;
  itemName: BilingualPair;
  referenceStandard: string | null;
  /** The catalogue's price TODAY, for comparison only. It changes nothing. */
  catalogueBasePriceMinor: number | null;
  /** What was agreed. This is the price that is used. */
  agreedPriceMinor: number;
  notes: string | null;
}

export interface PricingAgreementSummary {
  id: string;
  clientId: string;
  clientNumber: string;
  clientName: string;
  reference: string | null;
  title: string | null;
  validFrom: string;
  validUntil: string | null;
  isActive: boolean;
  /** Computed by the backend against the laboratory's own calendar day. */
  isEffectiveToday: boolean;
  lineCount: number;
  currency: string;
  createdAt: string;
}

export interface PricingAgreementDetail extends PricingAgreementSummary {
  notes: string | null;
  deactivatedAt: string | null;
  deactivationReason: string | null;
  updatedAt: string;
  lines: PricingAgreementLine[];
}

export interface QuotationParty {
  kind: 'client' | 'prospect';
  clientId: string | null;
  clientNumber: string | null;
  name: string;
  contactName: string | null;
  contactTitle: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  vatRegistrationNumber: string | null;
  commercialRegistrationNumber: string | null;
  addressLine: string | null;
  city: string | null;
  countryCode: string | null;
}

/** The client contact a quotation is addressed to, as the client file holds it. */
export interface QuotationContact {
  id: string;
  name: string;
  title: string | null;
  phone: string | null;
  email: string | null;
}

export interface QuotationLine {
  id: string;
  lineNumber: number;
  catalogueItemId: string | null;
  itemCode: string | null;
  description: BilingualPair;
  referenceStandard: string | null;
  standardVersion: string | null;
  pricingUnit: string | null;
  quantity: number;
  /** What the catalogue said AT THE TIME. Null is "there was none". */
  cataloguePriceMinor: number | null;
  agreementPriceMinor: number | null;
  unitPriceMinor: number;
  /** `catalogue`, `agreement` or `override`. */
  priceSource: string;
  priceOverrideReason: string | null;
  discountPercent: number | null;
  discountAmountMinor: number | null;
  subtotalMinor: number;
  discountMinor: number;
  totalMinor: number;
  notes: string | null;
}

export interface QuotationTotals {
  subtotalMinor: number;
  lineDiscountMinor: number;
  documentDiscountMinor: number;
  discountMinor: number;
  taxableMinor: number;
  taxMinor: number;
  totalMinor: number;
}

export interface QuotationSummary {
  id: string;
  quotationNumber: string;
  party: QuotationParty;
  projectId: string | null;
  /** The STORED status — one of the four a person causes. */
  status: string;
  /**
   * What to SHOW. `expired` when a sent quotation is past its validity date.
   * Derived by the backend from the date, never stored, so this interface
   * renders THIS rather than recomputing the rule for itself.
   */
  effectiveStatus: string;
  isExpired: boolean;
  issueDate: string;
  validUntil: string;
  currency: string;
  totalMinor: number;
  createdAt: string;
  /** A rejected quotation a Manager deleted (D210). Kept, read-only. */
  isDeleted: boolean;
}

/**
 * What "Create New From This Quotation" could not carry over unchanged
 * (D209). Every difference is named so nothing changes silently.
 * `repricedLines[].lineNumber` is the new draft's line; `skippedLines` names
 * the SOURCE's line, which has no counterpart on the new draft.
 */
export interface QuotationCopyNotes {
  repricedLines: {
    lineNumber: number;
    itemCode: string | null;
    sourceUnitPriceMinor: number;
    unitPriceMinor: number;
    reason: 'price_changed' | 'override_not_carried';
  }[];
  skippedLines: {
    lineNumber: number;
    itemCode: string | null;
    reason: 'service_unavailable' | 'price_not_set' | 'override_not_carried';
  }[];
  droppedReferences: ('project' | 'contact' | 'location')[];
}

export interface QuotationDetail extends QuotationSummary {
  subject: string | null;
  /** General conditions. */
  terms: BilingualPair;
  /** INTERNAL. Never printed on the document. */
  notes: string | null;
  /** What the document prints beyond its lines (D200). */
  inquiryReference: string | null;
  contact: QuotationContact | null;
  projectLocation: { id: string; name: string } | null;
  introduction: BilingualPair;
  paymentTerms: BilingualPair;
  scopeOfWork: string | null;
  remarks: string | null;
  /** Printed for the client, unlike `notes`. */
  additionalNotes: string | null;
  /** The person who created it. Never typed by hand. */
  preparedBy: { userId: string; displayName: string } | null;
  /** True once "record as sent" froze the document (D201). */
  hasIssuedDocument: boolean;
  /** Frozen on the document. Never the laboratory's current rate. */
  taxRatePercent: number;
  discountPercent: number | null;
  discountAmountMinor: number | null;
  discountReason: string | null;
  pricingAgreementId: string | null;
  totals: QuotationTotals;
  lines: QuotationLine[];
  isEditable: boolean;
  sentAt: string | null;
  sentChannel: string | null;
  sentNote: string | null;
  respondedAt: string | null;
  responseNote: string | null;
  /** Shown on the page: printing a quotation never moves its status. */
  printCount: number;
  lastPrintedAt: string | null;
  convertedClientId: string | null;
  convertedAt: string | null;
  prospectConversionPending: boolean;
  /** The quotation this one was created from (D209). */
  sourceQuotation: { id: string; quotationNumber: string } | null;
  /** Who deleted this rejected quotation, when and why (D210). */
  deletedAt: string | null;
  deletedBy: { id: string; name: string } | null;
  deletionReason: string | null;
  updatedAt: string;
}

export const catalogueApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    categoryId?: string;
    sampleType?: string;
    templateReadiness?: string;
    includeInactive?: boolean;
  }) =>
    apiRequest<PaginatedResponse<CatalogueItem>>('/catalogue', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        ...(query.categoryId ? { categoryId: query.categoryId } : {}),
        ...(query.sampleType ? { sampleType: query.sampleType } : {}),
        ...(query.templateReadiness
          ? { templateReadiness: query.templateReadiness }
          : {}),
        includeInactive: query.includeInactive === false ? 'false' : 'true',
      },
    }),

  get: (id: string) => apiRequest<CatalogueItem>(`/catalogue/${id}`),

  create: (input: Record<string, unknown>) =>
    apiRequest<CatalogueItem>('/catalogue', { method: 'POST', body: input }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<CatalogueItem>(`/catalogue/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  listCategories: (includeInactive = true) =>
    apiRequest<TestCategory[]>('/catalogue/categories', {
      query: { includeInactive: includeInactive ? 'true' : 'false' },
    }),
};

export const pricingAgreementsApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    clientId?: string;
    includeInactive?: boolean;
  }) =>
    apiRequest<PaginatedResponse<PricingAgreementSummary>>(
      '/pricing-agreements',
      {
        query: {
          page: query.page ?? 1,
          pageSize: query.pageSize ?? 25,
          ...(query.clientId ? { clientId: query.clientId } : {}),
          includeInactive: query.includeInactive === false ? 'false' : 'true',
        },
      },
    ),

  get: (id: string) =>
    apiRequest<PricingAgreementDetail>(`/pricing-agreements/${id}`),

  create: (input: Record<string, unknown>) =>
    apiRequest<PricingAgreementDetail>('/pricing-agreements', {
      method: 'POST',
      body: input,
    }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<PricingAgreementDetail>(`/pricing-agreements/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  /**
   * Replace the agreed prices.
   *
   * A set operation: an agreement is renegotiated as a whole, and the audit
   * entry records the prices before and after. Nothing already quoted moves —
   * a quotation line stores the price it used.
   */
  setLines: (
    id: string,
    input: {
      lines: { catalogueItemId: string; agreedPriceMinor: number }[];
      reason?: string;
    },
  ) =>
    apiRequest<PricingAgreementDetail>(`/pricing-agreements/${id}/lines`, {
      method: 'PUT',
      body: input,
    }),
};

/**
 * The services and prices an accepted quotation hands to a work order.
 *
 * The work order raised from it, where one has been. Two named shapes rather
 * than a nullable field, so "none has been raised" is something the API SAYS
 * rather than something the interface infers from an absence.
 */
export interface QuotationHandover {
  quotationId: string;
  quotationNumber: string;
  /** The converted client, where the quotation began with a prospect. */
  clientId: string | null;
  currency: string;
  services: QuotationLine[];
  totals: QuotationTotals;
  workOrder:
    | { state: 'raised'; id: string; workOrderNumber: string; raisedAt: string }
    | { state: 'none' };
}

export const quotationsApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    clientId?: string;
    status?: string;
    expired?: boolean;
    prospectsOnly?: boolean;
    /** Needs `quotations.delete`; the API refuses it otherwise (D214). */
    includeDeleted?: boolean;
  }) =>
    apiRequest<PaginatedResponse<QuotationSummary>>('/quotations', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        ...(query.clientId ? { clientId: query.clientId } : {}),
        ...(query.status ? { status: query.status } : {}),
        ...(query.expired === undefined
          ? {}
          : { expired: query.expired ? 'true' : 'false' }),
        prospectsOnly: query.prospectsOnly ? 'true' : 'false',
        ...(query.includeDeleted ? { includeDeleted: 'true' } : {}),
      },
    }),

  get: (id: string) => apiRequest<QuotationDetail>(`/quotations/${id}`),

  create: (input: Record<string, unknown>) =>
    apiRequest<QuotationDetail>('/quotations', { method: 'POST', body: input }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<QuotationDetail>(`/quotations/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  setLines: (id: string, input: Record<string, unknown>) =>
    apiRequest<QuotationDetail>(`/quotations/${id}/lines`, {
      method: 'PUT',
      body: input,
    }),

  /**
   * Record that the quotation was DELIVERED by a person.
   *
   * This product transmits nothing — there is no mail transport in it. The
   * channel says what actually happened, because a send result must be the
   * actual result.
   */
  send: (id: string, input: { channel: string; note?: string }) =>
    apiRequest<QuotationDetail>(`/quotations/${id}/send`, {
      method: 'POST',
      body: input,
    }),

  /**
   * Record that a copy was printed.
   *
   * CHANGES NO STATUS. It is a separate call from `recordResponse` precisely
   * so that printing can never be mistaken for — or accidentally wired to —
   * acceptance.
   */
  /**
   * The quotation DOCUMENT: the A4 PDF the server renders from its own
   * template (D198). The same bytes serve the preview, the printed copy and the
   * downloaded file. Fetching it changes nothing.
   */
  document: (id: string, language: 'ar' | 'en', download = false) =>
    apiDownload(
      `/quotations/${id}/document?language=${language}${download ? '&download=1' : ''}`,
    ),

  recordPrint: (id: string, input: { locale?: string } = {}) =>
    apiRequest<QuotationDetail>(`/quotations/${id}/print`, {
      method: 'POST',
      body: input,
    }),

  /** The client's ACTUAL answer. The only route to accepted or rejected. */
  recordResponse: (id: string, input: Record<string, unknown>) =>
    apiRequest<QuotationDetail>(`/quotations/${id}/response`, {
      method: 'POST',
      body: input,
    }),

  /** Give the prospect on an accepted quotation a client file. No re-entry. */
  convertProspect: (id: string, input: Record<string, unknown>) =>
    apiRequest<QuotationDetail>(`/quotations/${id}/convert-prospect`, {
      method: 'POST',
      body: input,
    }),

  /**
   * "Create New From This Quotation" (D209): a new, independent DRAFT. The
   * source is not changed. `copyNotes` names every line priced differently,
   * every line left out and every reference dropped.
   */
  copy: (id: string) =>
    apiRequest<QuotationDetail & { copyNotes: QuotationCopyNotes }>(`/quotations/${id}/copy`, {
      method: 'POST',
      body: {},
    }),

  /** Soft-delete a REJECTED quotation, with a mandatory reason (D210). */
  remove: (id: string, input: { reason: string }) =>
    apiRequest<QuotationDetail>(`/quotations/${id}/delete`, {
      method: 'POST',
      body: input,
    }),

  /**
   * What an accepted quotation carries onward, and whether it already has.
   *
   * `workOrder` is a FACT in one of two shapes that say different things: a
   * named work order that was raised, or plainly none. It is never an empty
   * list standing in for "we do not know".
   */
  handover: (id: string) =>
    apiRequest<QuotationHandover>(`/quotations/${id}/handover`),
};

// ---------------------------------------------------------------------------
// Finance — invoices, payments and statements (Phase 11)
//
// Every view type is imported from @lims/shared-types, never restated: F
// already publishes InvoiceView, InvoiceDetailView, PaymentView,
// PaymentDetailView, BillableServiceView and StatementView with the exact
// fields the backend returns. This file only adds the request shapes.
// ---------------------------------------------------------------------------

export const invoicesApi = {
  /** Work-order service lines not yet fully billed (D150). */
  billableServices: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    clientId?: string;
    workOrderId?: string;
    projectId?: string;
    /** Defaults to true on the server: lines with nothing left are hidden. */
    onlyRemaining?: boolean;
  }) =>
    apiRequest<PaginatedResponse<BillableServiceView>>('/billable-services', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        ...(query.clientId ? { clientId: query.clientId } : {}),
        ...(query.workOrderId ? { workOrderId: query.workOrderId } : {}),
        ...(query.projectId ? { projectId: query.projectId } : {}),
        ...(query.onlyRemaining === undefined
          ? {}
          : { onlyRemaining: query.onlyRemaining ? 'true' : 'false' }),
      },
    }),

  list: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    clientId?: string;
    status?: string;
    paymentStatus?: string;
    overdue?: boolean;
    issuedFrom?: string;
    issuedTo?: string;
  }) =>
    apiRequest<PaginatedResponse<InvoiceView>>('/invoices', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        ...(query.clientId ? { clientId: query.clientId } : {}),
        ...(query.status ? { status: query.status } : {}),
        ...(query.paymentStatus ? { paymentStatus: query.paymentStatus } : {}),
        ...(query.overdue === undefined
          ? {}
          : { overdue: query.overdue ? 'true' : 'false' }),
        ...(query.issuedFrom ? { issuedFrom: query.issuedFrom } : {}),
        ...(query.issuedTo ? { issuedTo: query.issuedTo } : {}),
      },
    }),

  get: (id: string) => apiRequest<InvoiceDetailView>(`/invoices/${id}`),

  /** Open a draft. The client cannot change afterwards. */
  create: (input: Record<string, unknown>) =>
    apiRequest<InvoiceDetailView>('/invoices', { method: 'POST', body: input }),

  /** Edit a draft against the revision the editor last read (D152). */
  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<InvoiceDetailView>(`/invoices/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  /** Allocates the number (D143). Refuses a stale revision (draft_changed). */
  issue: (id: string, input: { draftRevision: number }) =>
    apiRequest<InvoiceDetailView>(`/invoices/${id}/issue`, {
      method: 'POST',
      body: input,
    }),

  /** A draft only. Keeps the row; frees the quantities it reserved. */
  discard: (id: string, input: { reason: string }) =>
    apiRequest<InvoiceDetailView>(`/invoices/${id}/discard`, {
      method: 'POST',
      body: input,
    }),

  /** An issued invoice with no active allocation. Keeps the row and number. */
  cancel: (id: string, input: { reason: string }) =>
    apiRequest<InvoiceDetailView>(`/invoices/${id}/cancel`, {
      method: 'POST',
      body: input,
    }),

  /** A credit or debit against an issued invoice (D154). Nothing is deleted. */
  recordAdjustment: (
    id: string,
    input: { kind: string; amountMinor: number; reason: string },
  ) =>
    apiRequest<InvoiceDetailView>(`/invoices/${id}/adjustments`, {
      method: 'POST',
      body: input,
    }),
};

export const paymentsApi = {
  list: (query: {
    page?: number;
    pageSize?: number;
    search?: string;
    clientId?: string;
    status?: string;
    receivedFrom?: string;
    receivedTo?: string;
  }) =>
    apiRequest<PaginatedResponse<PaymentView>>('/payments', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.search ? { search: query.search } : {}),
        ...(query.clientId ? { clientId: query.clientId } : {}),
        ...(query.status ? { status: query.status } : {}),
        ...(query.receivedFrom ? { receivedFrom: query.receivedFrom } : {}),
        ...(query.receivedTo ? { receivedTo: query.receivedTo } : {}),
      },
    }),

  get: (id: string) => apiRequest<PaymentDetailView>(`/payments/${id}`),

  /** Money received. What the allocations leave unallocated is credit. */
  record: (input: Record<string, unknown>) =>
    apiRequest<PaymentDetailView>('/payments', { method: 'POST', body: input }),

  /** Allocate (more of) a payment's unallocated amount. */
  allocate: (
    id: string,
    input: { allocations: { invoiceId: string; amountMinor: number }[] },
  ) =>
    apiRequest<PaymentDetailView>(`/payments/${id}/allocations`, {
      method: 'POST',
      body: input,
    }),

  /** Stays on the record, with its reason (D153). Never deleted. */
  reverseAllocation: (
    id: string,
    allocationId: string,
    input: { reason: string },
  ) =>
    apiRequest<PaymentDetailView>(
      `/payments/${id}/allocations/${allocationId}/reverse`,
      { method: 'POST', body: input },
    ),

  /** Reverses every active allocation in the same act, with the same reason. */
  reverse: (id: string, input: { reason: string }) =>
    apiRequest<PaymentDetailView>(`/payments/${id}/reverse`, {
      method: 'POST',
      body: input,
    }),
};

export const statementsApi = {
  /**
   * A client's statement (D155): opening balance, the period's rows, closing
   * balance. Every omitted parameter is filled in by the backend, which names
   * each default it applied in `defaultsApplied` — never repeated here.
   */
  get: (
    clientId: string,
    query: { currency?: string; from?: string; to?: string } = {},
  ) =>
    apiRequest<StatementView>(`/clients/${clientId}/statement`, {
      query: {
        ...(query.currency ? { currency: query.currency } : {}),
        ...(query.from ? { from: query.from } : {}),
        ...(query.to ? { to: query.to } : {}),
      },
    }),
};

// ---------------------------------------------------------------------------
// Branches, departments and work orders (Phase 6)
// ---------------------------------------------------------------------------

export interface Branch {
  id: string;
  code: string | null;
  name: string;
  addressLine: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  notes: string | null;
  isActive: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Department {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  branch: { id: string; name: string } | null;
  sortOrder: number;
  isActive: boolean;
  /** True while nothing has ever cited it, so it may still be deleted. */
  canDelete: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DepartmentWithUsage extends Department {
  /** Reported, never used to block. A laboratory may retire a department. */
  usage: { catalogueItems: number; requestedServices: number };
}

/**
 * One requested service on a work order.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING A FIELD HERE.
 * ===========================================================================
 *
 * A work order REQUESTS services. It does not describe how any of them is
 * performed. Note what is ABSENT, for every test including the five initial
 * ones: input fields, readings, units of measurement, formulas, calculation
 * order, rounding rules, acceptance limits, charts, Excel cell mappings and
 * specialised result layouts. `templateReadiness` — forwarded unchanged from
 * the catalogue entry — is the one thing this says about the science, and what
 * it says is that there is no specialised template.
 */
export interface WorkOrderService {
  id: string;
  lineNumber: number;
  catalogueItemId: string | null;
  /** Where it came from, where it came from a quotation. */
  quotationLineId: string | null;
  itemCode: string | null;
  description: BilingualPair;
  referenceStandard: string | null;
  standardVersion: string | null;
  /** The department's ONE name (D183). */
  department: { id: string; name: string } | null;
  pricingUnit: string | null;
  quantity: number;
  /** What the catalogue said AT THE TIME. Null is "there was none". */
  cataloguePriceMinor: number | null;
  agreementPriceMinor: number | null;
  unitPriceMinor: number;
  priceSource: string;
  priceOverrideReason: string | null;
  discountPercent: number | null;
  discountAmountMinor: number | null;
  subtotalMinor: number;
  discountMinor: number;
  totalMinor: number;
  templateReadiness: string;
  /** Computed by the backend. True for every entry in the product today. */
  usesGeneralWorkflowOnly: boolean;
  state: string;
  startedAt: string | null;
  completedAt: string | null;
  completionNote: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  notes: string | null;
}

/**
 * What is done and what remains.
 *
 * Counts rather than a percentage, because a percentage invites the reader to
 * round it to "finished".
 */
export interface WorkOrderProgress {
  total: number;
  requested: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  outstanding: number;
}

export interface WorkOrderSummary {
  id: string;
  workOrderNumber: string;
  clientId: string;
  clientNumber: string | null;
  clientName: string | null;
  projectId: string | null;
  /**
   * DERIVED by the backend from the states of the requested services, never
   * stored. This interface renders what it is told rather than recomputing it,
   * so the two cannot disagree.
   */
  status: string;
  isCancelled: boolean;
  isOverdue: boolean;
  priority: string;
  requestDate: string;
  /** Null means the laboratory promised no date. It is not "today". */
  dueDate: string | null;
  progress: WorkOrderProgress;
  currency: string;
  totalMinor: number;
  createdAt: string;
}

export interface WorkOrderDetail extends WorkOrderSummary {
  locationId: string | null;
  branch: { id: string; name: string } | null;
  quotationId: string | null;
  quotationNumber: string | null;
  pricingAgreementId: string | null;
  requestingParty: {
    name: string | null;
    contactId: string | null;
    email: string | null;
    phone: string | null;
  };
  purchaseOrderNumber: string | null;
  clientReference: string | null;
  subject: string | null;
  notes: string | null;
  taxRatePercent: number;
  discountPercent: number | null;
  discountAmountMinor: number | null;
  discountReason: string | null;
  totals: {
    subtotalMinor: number;
    lineDiscountMinor: number;
    documentDiscountMinor: number;
    discountMinor: number;
    taxableMinor: number;
    taxMinor: number;
    totalMinor: number;
  };
  services: WorkOrderService[];
  acceptsScopeChange: boolean;
  scopeChangeNeedsAcknowledgement: boolean;
  scopeChangeCount: number;
  lastScopeChangeAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  /**
   * Whether each section exists, never a count. `available` sections (samples,
   * assignments) are read from their own endpoints; a `not_implemented` one
   * carries its planned phase, never an empty list the system cannot vouch for.
   */
  sections: Record<string, { state: string; plannedPhase?: number }>;
  updatedAt: string;
}

export const organisationApi = {
  /** Readable by any signed-in member: the work-order form needs it. */
  listBranches: (includeInactive = true) =>
    apiRequest<{ data: Branch[] }>('/branches', {
      query: { includeInactive: String(includeInactive) },
    }),

  createBranch: (input: Record<string, unknown>) =>
    apiRequest<Branch>('/branches', { method: 'POST', body: input }),

  updateBranch: (id: string, input: Record<string, unknown>) =>
    apiRequest<Branch>(`/branches/${id}`, { method: 'PATCH', body: input }),

  listDepartments: (includeInactive = true) =>
    apiRequest<{ data: Department[] }>('/departments', {
      query: { includeInactive: String(includeInactive) },
    }),

  getDepartment: (id: string) =>
    apiRequest<DepartmentWithUsage>(`/departments/${id}`),

  createDepartment: (input: Record<string, unknown>) =>
    apiRequest<Department>('/departments', { method: 'POST', body: input }),

  updateDepartment: (id: string, input: Record<string, unknown>) =>
    apiRequest<Department>(`/departments/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  deleteDepartment: (id: string) =>
    apiRequest<void>(`/departments/${id}`, { method: 'DELETE' }),
};

export const workOrdersApi = {
  list: (query: Record<string, string | number | undefined> = {}) =>
    apiRequest<PaginatedResponse<WorkOrderSummary>>('/work-orders', { query }),

  get: (id: string) => apiRequest<WorkOrderDetail>(`/work-orders/${id}`),

  /** Raised `awaiting_sample`. No sample arrived, and nothing says one did. */
  create: (input: Record<string, unknown>) =>
    apiRequest<WorkOrderDetail>('/work-orders', {
      method: 'POST',
      body: input,
    }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<WorkOrderDetail>(`/work-orders/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  /**
   * Renegotiate the scope.
   *
   * Once work has started this REQUIRES the three impact acknowledgements, and
   * the backend refuses it without them. This interface asks for them; it does
   * not enforce the rule, because hiding a control is never a security
   * mechanism (CLAUDE.md section 5.1).
   */
  setServices: (id: string, input: Record<string, unknown>) =>
    apiRequest<WorkOrderDetail>(`/work-orders/${id}/services`, {
      method: 'PUT',
      body: input,
    }),

  /** Records where ONE service got to. Never marks the whole request done. */
  setServiceState: (
    id: string,
    serviceId: string,
    input: Record<string, unknown>,
  ) =>
    apiRequest<WorkOrderDetail>(
      `/work-orders/${id}/services/${serviceId}/state`,
      { method: 'PATCH', body: input },
    ),

  /** Records a reason and stops unexecuted work. Deletes nothing. */
  cancel: (id: string, input: { reason: string }) =>
    apiRequest<WorkOrderDetail>(`/work-orders/${id}/cancel`, {
      method: 'POST',
      body: input,
    }),
};

// ---------------------------------------------------------------------------
// Samples — Phase 7
// ---------------------------------------------------------------------------

/**
 * A sample, as the backend describes it.
 *
 * NOT ONE FIELD HERE IS A MEASUREMENT. A sample record describes a DELIVERY:
 * what arrived, when, from whom, how much of it there was in the laboratory's
 * own words, and what the laboratory decided about it. Dimensions, masses,
 * readings, limits and geometries belong to a test method, and no method has
 * been validated yet (CLAUDE.md section 4). The backend's merge-gate test
 * asserts this same list against the real response and the real table, so a
 * scientific field cannot arrive here quietly.
 */
export interface Sample {
  id: string;
  sampleNumber: string;
  workOrderId: string | null;
  jobNumber: string | null;
  sampleType: string;
  status: string;
  /**
   * DERIVED by the backend from the acceptance history. Rendered, never
   * recomputed: a second implementation of the rule could disagree with the
   * one the API enforces, and then the page would promise work the server
   * refuses.
   */
  mayBeAssignedTests: boolean;
  receivedAt: string;
  receivedFrom: string;
  quantity: number;
  quantityUnit: string;
  clientReference: string | null;
  sourceDescription: string | null;
  sampledAt: string | null;
  sampledBy: string | null;
  conditionOnReceipt: string | null;
  notes: string | null;
  /** What the sample IS, as distinct from its material and its source (0023, D289). */
  description: string | null;
  /**
   * The additional details its sample type asked for (0024), in field order,
   * with the labels they are read under. A field archived since still shows.
   */
  additionalFields: SampleAdditionalField[];
  /** The numbered handover it arrived in; null for a sample received before receipts (0013). */
  receipt: { id: string; receiptNumber: string } | null;
  /**
   * Set when the sample was received without a work order and linked to one
   * afterwards (D324). Its receipt still names none.
   */
  workOrderLink?: {
    linkedAt: string;
    linkedBy: { id: string | null; displayName: string | null };
  } | null;
  /**
   * The work order the sample belongs to NOW, with that work order's client
   * and project (D325). The current context, read from the work order; the
   * receipt is history and may name none.
   */
  workOrder?: {
    id: string;
    workOrderNumber: string;
    client: { id: string; name: string } | null;
    project: { id: string; name: string } | null;
  } | null;
  /**
   * The work-order services requested for THIS sample (D215, D220). Empty
   * means NOT RECORDED — never "every service of the work order".
   */
  requestedServices: SampleRequestedService[];
  /** The signed-in person who recorded the arrival. */
  receivedBy: { id: string; displayName: string | null } | null;
  /** The LATEST decision. Null means nobody has decided yet. */
  currentDecision: SampleDecision | null;
  pieceCount: number;
  storage: SampleStorageState | null;
  /**
   * Computed by the backend from this laboratory's retention setting, at read
   * time. Null means this sample never becomes due, and nothing acts on it in
   * any case: disposal is always a recorded human act.
   */
  disposalDueAt: string | null;
  isDisposed: boolean;
  /**
   * A voided ("deleted") sample (D211): kept, read-only, out of the normal
   * lists. Who voided it, when and why come from the server.
   */
  isVoided: boolean;
  voidedAt: string | null;
  voidedBy: { id: string; displayName: string | null } | null;
  voidReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SampleDecision {
  id: string;
  decision: string;
  reason: string | null;
  notes: string | null;
  decidedAt: string;
  decidedBy: { id: string; displayName: string | null };
}

export interface SamplePiece {
  id: string;
  pieceCode: string;
  pieceIndex: number;
  label: string | null;
  notes: string | null;
  storageLocation: string | null;
  createdAt: string;
}

export interface SampleStorageState {
  location: string | null;
  shelfOrBin: string | null;
  note: string | null;
  disposedAt: string | null;
  disposalMethod: string | null;
  disposalWitness: string | null;
}

export interface SampleMovement {
  id: string;
  movementType: string;
  pieceId: string | null;
  fromLocation: string | null;
  toLocation: string | null;
  fromDepartmentId: string | null;
  toDepartmentId: string | null;
  handedToUserId: string | null;
  handedToName: string | null;
  note: string | null;
  /** When it happened, and when it was written down. Both, deliberately. */
  occurredAt: string;
  recordedAt: string;
  recordedBy: { id: string; displayName: string | null };
}

/**
 * The CONTENT of a label, never a layout.
 *
 * The backend decides what a label says; this application decides how it looks
 * and the browser prints it (decision D69). `widthMm` and `heightMm` are the
 * laboratory's own configured stock size, so the preview is rendered at the
 * real physical size rather than at a size this code chose.
 */
export interface LabelContent {
  sampleNumber: string;
  pieceCode: string | null;
  jobNumber: string | null;
  sampleType: string;
  receivedAt: string;
  symbology: string;
  /** Exactly the Sample ID, or the piece code. What a scanner types. */
  scanValue: string;
  qrPayload: {
    sampleNumber: string;
    pieceCode: string | null;
    jobNumber: string | null;
    sampleType: string;
    extras: Record<string, string>;
  };
  widthMm: number;
  heightMm: number;
  extraLines: string[];
  /** Including this one, once a print has been recorded. */
  printCount: number;
}

export interface SampleTypeSettings {
  sampleType: string;
  requiredFields: string[];
  /** Null means: keep it until a person disposes of it. Never a schedule. */
  retentionDays: number | null;
}

/** One numbered handover and the samples it brought (D193). Read-only. */

export interface SampleReceipt {
  id: string;
  receiptNumber: string;
  receivedAt: string;
  receivedFrom: string;
  receivedBy: { id: string; displayName: string | null };
  workOrder: { id: string; workOrderNumber: string } | null;
  /** The job's client, project and site, read through the work order (D213). */
  client: { id: string; clientNumber: string | null; name: string } | null;
  project: { id: string; name: string; reference: string | null } | null;
  location: { id: string; name: string; address: string | null; city: string | null } | null;
  /** Notes about the handover as a whole (D197); null when none were given. */
  notes: string | null;
  createdAt: string;
  samples: {
    id: string;
    sampleNumber: string;
    sampleType: string;
    quantity: number;
    quantityUnit: string;
    sourceDescription: string | null;
    /** What the sample IS (D289). */
    description: string | null;
    clientReference: string | null;
    conditionOnReceipt: string | null;
    status: string;
    currentDecision: { decision: string; reason: string | null } | null;
    /** A voided sample stays on its receipt, marked as voided (D211). */
    isVoided: boolean;
    voidedAt: string | null;
    /**
     * Its requested tests, in work-order line order (D220). EMPTY means they
     * were not recorded — never "every service of the work order".
     */
    requestedServices: SampleRequestedService[];
  }[];
}

/** One row of the Sample Receipts register (D213). */
export interface SampleReceiptRow {
  id: string;
  receiptNumber: string;
  receivedAt: string;
  /** Delivered by, as stated at the counter. */
  receivedFrom: string;
  receivedBy: { id: string; displayName: string | null };
  workOrder: { id: string; workOrderNumber: string } | null;
  client: { id: string; name: string } | null;
  project: { id: string; name: string } | null;
  sampleCount: number;
  voidedCount: number;
  createdAt: string;
}

/** The job as the receiving desk sees it — derived, never retyped (D194). */
/**
 * One test requested for a sample (D220): the work-order service's frozen
 * name, code and standard. Deliberately no price, quantity or discount.
 */
export interface SampleRequestedService {
  workOrderServiceId: string;
  lineNumber: number;
  catalogueItemId: string | null;
  itemCode: string | null;
  descriptionAr: string | null;
  descriptionEn: string | null;
  referenceStandard: string | null;
  standardVersion: string | null;
  state: string;
}

export interface ReceivingContext {
  workOrder: {
    id: string;
    workOrderNumber: string;
    requestDate: string;
    clientReference: string | null;
    purchaseOrderNumber: string | null;
    isCancelled: boolean;
  };
  client: {
    id: string;
    clientNumber: string | null;
    name: string | null;
    address: string | null;
    city: string | null;
    contactPhone: string | null;
  };
  project: { id: string; name: string | null; reference: string | null } | null;
  location: { id: string; name: string | null; address: string | null; city: string | null } | null;
  requestingParty: { name: string | null; phone: string | null; email: string | null };
  services: {
    id: string;
    lineNumber: number;
    itemCode: string | null;
    description: BilingualPair;
    referenceStandard: string | null;
    standardVersion: string | null;
    quantity: number;
    state: string;
  }[];
}

/** A receiving operation's outcome: the receipt, and the samples it registered, in order (D197). */
export interface ReceiveSamplesResult {
  receipt: SampleReceipt;
  received: { id: string; sampleNumber: string }[];
}

export const sampleReceiptsApi = {
  /** The Sample Receipts register, newest first (D213). */
  list: (query: Record<string, string | number | boolean | undefined> = {}) =>
    apiRequest<PaginatedResponse<SampleReceiptRow>>('/sample-receipts', { query }),
  get: (id: string) => apiRequest<SampleReceipt>(`/sample-receipts/${id}`),
  /**
   * The receipt DOCUMENT: an A4 PDF the server renders on every request
   * (D213). The same bytes serve printing and saving. Fetching it changes nothing.
   */
  document: (id: string, language: 'ar' | 'en', download = false) =>
    apiDownload(
      `/sample-receipts/${id}/document?language=${language}${download ? '&download=1' : ''}`,
    ),
  /**
   * Receive several samples delivered together (D197): one new receipt, one
   * Sample ID per line — all registered, or none.
   */
  receive: (input: Record<string, unknown>) =>
    apiRequest<ReceiveSamplesResult>('/sample-receipts', { method: 'POST', body: input }),
  /** More samples of the same delivery, on a receipt already issued (D197). */
  addSamples: (id: string, input: Record<string, unknown>) =>
    apiRequest<ReceiveSamplesResult>(`/sample-receipts/${id}/samples`, {
      method: 'POST',
      body: input,
    }),
  /** Read with the permission to RECEIVE. */
  workOrderContext: (workOrderId: string) =>
    apiRequest<ReceivingContext>(`/sample-receipts/work-order-context/${workOrderId}`),
};

/**
 * One work order of a register page (D320); `id: null` is the samples linked
 * to no work order. The counts cover every page of the same filters.
 */
export interface SampleWorkOrderGroup {
  id: string | null;
  number: string | null;
  clientName: string | null;
  projectName: string | null;
  createdAt: string | null;
  sampleCount: number;
  statusCounts: Record<string, number>;
}

export interface SampleListResponse extends PaginatedResponse<Sample> {
  // Always sent by the register; optional so a caller that ignores it need not mock it.
  meta: PaginatedResponse<Sample>['meta'] & {
    workOrders?: SampleWorkOrderGroup[];
    /**
     * Present with `pageBy=workOrder` (D321): `total` and `totalPages` then
     * count GROUPS, `sampleTotal` the samples, and each group shows at most
     * `groupSampleLimit` of its samples.
     */
    pageBy?: 'workOrder';
    sampleTotal?: number;
    groupSampleLimit?: number;
  };
}

/** One choice of the register's work order filter (D322). */
export interface SampleWorkOrderOption {
  id: string;
  number: string;
  clientName: string | null;
  projectName: string | null;
  createdAt: string;
  sampleCount: number;
}

export const samplesApi = {
  list: (
    query: Record<string, string | number | boolean | undefined> = {},
  ) => apiRequest<SampleListResponse>('/samples', { query }),

  get: (id: string) => apiRequest<Sample>(`/samples/${id}`),

  /**
   * The register's work order filter (D322): this laboratory's work orders
   * that have samples, newest first, searched on the server.
   */
  workOrderOptions: (
    query: { search?: string; limit?: number; purpose?: 'register' | 'link' } = {},
  ) =>
    apiRequest<{ data: SampleWorkOrderOption[] }>('/samples/work-order-options', { query }),

  /**
   * Register a sample that has arrived. The Sample ID is issued HERE, before
   * any acceptance decision exists (decision D72), so the receiving trace
   * survives whatever the laboratory later concludes.
   */
  receive: (input: Record<string, unknown>) =>
    apiRequest<Sample>('/samples', { method: 'POST', body: input }),

  /**
   * Edit Sample (D222): the material, the descriptive facts, the storage
   * location and `requestedServiceIds` (the complete new set) in one request.
   * The number, the arrival time and the work order cannot change.
   */
  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<Sample>(`/samples/${id}`, { method: 'PATCH', body: input }),

  /**
   * Correct the tests requested for a sample (D217): the complete new set.
   * A test whose work on this sample is not cancelled cannot be removed
   * (D225). The interface uses `update` instead; kept for the API's own sake.
   */
  setRequestedServices: (id: string, workOrderServiceIds: readonly string[]) =>
    apiRequest<Sample>(`/samples/${id}/requested-services`, {
      method: 'PUT',
      body: { workOrderServiceIds },
    }),

  /**
   * "Delete Sample": a VOID with a reason (D211). The number, the receipt and
   * all history are kept; the sample takes no further work.
   */
  void: (id: string, input: { reason: string }) =>
    apiRequest<Sample>(`/samples/${id}/void`, { method: 'POST', body: input }),

  /** Appends a decision. It never edits the one before it. */
  decide: (id: string, input: Record<string, unknown>) =>
    apiRequest<Sample>(`/samples/${id}/decision`, {
      method: 'POST',
      body: input,
    }),

  decisions: (id: string) =>
    apiRequest<SampleDecision[]>(`/samples/${id}/decisions`),

  pieces: (id: string) => apiRequest<SamplePiece[]>(`/samples/${id}/pieces`),

  addPieces: (id: string, input: Record<string, unknown>) =>
    apiRequest<SamplePiece[]>(`/samples/${id}/pieces`, {
      method: 'POST',
      body: input,
    }),

  updatePiece: (id: string, pieceId: string, input: Record<string, unknown>) =>
    apiRequest<SamplePiece>(`/samples/${id}/pieces/${pieceId}`, {
      method: 'PATCH',
      body: input,
    }),

  /** Reads what a label would say. Records NOTHING: previewing is not printing. */
  label: (id: string, query: { pieceId?: string; locale?: string } = {}) =>
    apiRequest<LabelContent>(`/samples/${id}/label`, { query }),

  /** Records that a label was printed. Mints no new identity. */
  recordPrint: (id: string, input: Record<string, unknown>) =>
    apiRequest<LabelContent>(`/samples/${id}/label/print`, {
      method: 'POST',
      body: input,
    }),

  movements: (id: string) =>
    apiRequest<SampleMovement[]>(`/samples/${id}/movements`),

  recordMovement: (id: string, input: Record<string, unknown>) =>
    apiRequest<SampleMovement>(`/samples/${id}/movements`, {
      method: 'POST',
      body: input,
    }),

  setStorage: (id: string, input: Record<string, unknown>) =>
    apiRequest<Sample>(`/samples/${id}/storage`, {
      method: 'PUT',
      body: input,
    }),

  /** Records a physical disposal. Destroys the material, never the record. */
  dispose: (id: string, input: Record<string, unknown>) =>
    apiRequest<Sample>(`/samples/${id}/disposal`, {
      method: 'POST',
      body: input,
    }),
};

/**
 * Per-sample-type policy.
 *
 * An empty list is the CORRECT answer for a laboratory that has configured
 * nothing: the product ships no rule for any material, because seeding one
 * would be inventing a method requirement (decision D70, CLAUDE.md section 4).
 */
/** One additional detail of a sample (0024). */
export interface SampleAdditionalField {
  fieldId: string;
  key: string;
  fieldType: SampleFieldTypeName;
  labelAr: string | null;
  labelEn: string | null;
  archived: boolean;
  /** Text, `YYYY-MM-DD`, `HH:MM`, an ISO instant, an exact decimal as text, yes/no, or an option id. */
  value: string | boolean;
  option: { id: string; value: string; labelAr: string | null; labelEn: string | null; archived: boolean } | null;
}

export type SampleFieldTypeName =
  | 'text'
  | 'long_text'
  | 'number'
  | 'date'
  | 'time'
  | 'datetime'
  | 'select'
  | 'boolean';

export interface SampleTypeOption {
  id: string;
  value: string;
  labelAr: string | null;
  labelEn: string | null;
  sortOrder: number;
  archived: boolean;
  usageCount: number;
}

export interface SampleTypeField {
  id: string;
  key: string;
  fieldType: SampleFieldTypeName;
  labelAr: string | null;
  labelEn: string | null;
  placeholderAr: string | null;
  placeholderEn: string | null;
  helpAr: string | null;
  helpEn: string | null;
  isRequired: boolean;
  sortOrder: number;
  archived: boolean;
  usageCount: number;
  options: SampleTypeOption[];
}

/** A laboratory's own sample type and its additional intake fields (0024). */
export interface SampleTypeDefinition {
  id: string;
  /** What samples carry as `sampleType`. Fixed at creation. */
  code: string;
  nameAr: string | null;
  nameEn: string | null;
  sortOrder: number;
  archived: boolean;
  archivedAt: string | null;
  /** One of the seven baseline types (D307): archived, never deleted. */
  isSystem: boolean;
  usageCount: number;
  /** Catalogue entries classified by this type (D306). */
  catalogueUsageCount: number;
  fields: SampleTypeField[];
}

/** The body of a field save: its definition, and for a Select the complete option list. */
export interface SampleTypeFieldInput {
  key?: string;
  fieldType?: SampleFieldTypeName;
  labelAr?: string | null;
  labelEn?: string | null;
  placeholderAr?: string | null;
  placeholderEn?: string | null;
  helpAr?: string | null;
  helpEn?: string | null;
  isRequired?: boolean;
  options?: { id?: string; value?: string; labelAr?: string | null; labelEn?: string | null; archived?: boolean }[];
}

/**
 * The sample type builder (0024). Reading is open to whoever receives or views
 * samples; changing needs `settings.update`, which the server enforces.
 */
export const sampleTypesApi = {
  list: () => apiRequest<SampleTypeDefinition[]>('/sample-types'),
  create: (input: { code?: string; nameAr?: string | null; nameEn?: string | null; fields?: SampleTypeFieldInput[] }) =>
    apiRequest<SampleTypeDefinition>('/sample-types', { method: 'POST', body: input }),
  update: (id: string, input: { nameAr?: string | null; nameEn?: string | null }) =>
    apiRequest<SampleTypeDefinition>(`/sample-types/${id}`, { method: 'PATCH', body: input }),
  archive: (id: string) => apiRequest<SampleTypeDefinition>(`/sample-types/${id}/archive`, { method: 'POST' }),
  restore: (id: string) => apiRequest<SampleTypeDefinition>(`/sample-types/${id}/restore`, { method: 'POST' }),
  remove: (id: string) => apiRequest<void>(`/sample-types/${id}`, { method: 'DELETE' }),
  addField: (id: string, input: SampleTypeFieldInput) =>
    apiRequest<SampleTypeDefinition>(`/sample-types/${id}/fields`, { method: 'POST', body: input }),
  updateField: (id: string, fieldId: string, input: SampleTypeFieldInput) =>
    apiRequest<SampleTypeDefinition>(`/sample-types/${id}/fields/${fieldId}`, { method: 'PATCH', body: input }),
  archiveField: (id: string, fieldId: string) =>
    apiRequest<SampleTypeDefinition>(`/sample-types/${id}/fields/${fieldId}/archive`, { method: 'POST' }),
  restoreField: (id: string, fieldId: string) =>
    apiRequest<SampleTypeDefinition>(`/sample-types/${id}/fields/${fieldId}/restore`, { method: 'POST' }),
  removeField: (id: string, fieldId: string) =>
    apiRequest<SampleTypeDefinition>(`/sample-types/${id}/fields/${fieldId}`, { method: 'DELETE' }),
  reorderFields: (id: string, ids: string[]) =>
    apiRequest<SampleTypeDefinition>(`/sample-types/${id}/field-order`, { method: 'PUT', body: { ids } }),
};

export const sampleTypeSettingsApi = {
  list: () => apiRequest<SampleTypeSettings[]>('/sample-type-settings'),

  setRequiredFields: (input: {
    sampleType: string;
    requiredFields: string[];
  }) =>
    apiRequest<SampleTypeSettings>('/sample-type-settings/required-fields', {
      method: 'PUT',
      body: input,
    }),

  setRetention: (input: { sampleType: string; retentionDays: number | null }) =>
    apiRequest<SampleTypeSettings>('/sample-type-settings/retention', {
      method: 'PUT',
      body: input,
    }),
};

// ---------------------------------------------------------------------------
// Phase 8 — sample attachments, assignment, general execution, equipment,
// specifications and the dashboard
//
// READ CLAUDE.md SECTION 4. The general result is a summary in each language,
// general notes and two execution dates. There is no reading, unit, formula,
// limit or chart in any type below, for any test.
// ---------------------------------------------------------------------------

/**
 * Sample photos and files (carried from Phase 7, decision D80).
 *
 * Served ONLY through the sample's own routes, against `samples.view`; the
 * generic files API refuses these files (decision D92).
 */
export const sampleAttachmentsApi = {
  list: (sampleId: string) =>
    apiRequest<StoredFile[]>(`/samples/${sampleId}/attachments`),

  upload: (sampleId: string, file: File, description?: string) => {
    const form = new FormData();
    form.append('file', file, file.name);
    if (description) form.append('description', description);
    return apiRequest<StoredFile>(`/samples/${sampleId}/attachments`, {
      method: 'POST',
      formData: form,
    });
  },

  download: (sampleId: string, fileId: string) =>
    apiDownload(`/samples/${sampleId}/attachments/${fileId}/content`),

  remove: (sampleId: string, fileId: string, reason: string) =>
    apiRequest<StoredFile>(`/samples/${sampleId}/attachments/${fileId}/remove`, {
      method: 'POST',
      body: { reason },
    }),
};

export interface PersonRef {
  id: string;
  displayName: string | null;
}

export interface Assignment {
  id: string;
  status: string;
  sample: {
    id: string;
    sampleNumber: string;
    sampleType: string;
    status: string;
    /** The sample's own work order (D326); null for one linked to none. */
    workOrderId?: string | null;
    jobNumber: string | null;
  };
  pieceId: string | null;
  pieceCode: string | null;
  test: {
    catalogueItemId: string;
    code: string | null;
    nameAr: string | null;
    nameEn: string | null;
    /** The catalogue test's standard designation (D326). */
    referenceStandard?: string | null;
    /** The CURRENT readiness of the test's specialised template. */
    templateReadiness: string;
  };
  workOrderServiceId: string | null;
  specificationId: string | null;
  specificationText: string | null;
  departmentId: string | null;
  assignedTo: PersonRef | null;
  priority: string;
  /** The ORIGINAL commitment. Never overwritten by a delay. */
  dueDate: string | null;
  /** The latest expected date a person recorded. Null when none was. */
  expectedDate: string | null;
  isDelayed: boolean;
  latestDelay: {
    reason: string | null;
    previousExpectedDate: string | null;
    newExpectedDate: string | null;
    recordedAt: string;
    recordedBy: PersonRef;
  } | null;
  latestStop: {
    reason: string | null;
    expectedDate: string | null;
    recordedAt: string;
    recordedBy: PersonRef;
  } | null;
  instructions: string | null;
  duplicateOfAssignmentId: string | null;
  duplicateReason: string | null;
  openAttemptId: string | null;
  attemptCount: number;
  cancelledAt: string | null;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One work order of the work list paged by work order (D326). */
export interface AssignmentWorkOrderGroup {
  id: string | null;
  number: string | null;
  clientName: string | null;
  projectName: string | null;
  createdAt: string | null;
  sampleCount: number;
  itemCount: number;
  statusCounts: Record<string, number>;
}

export interface AssignmentListResponse extends PaginatedResponse<Assignment> {
  /**
   * Present with `pageBy=workOrder` (D326): `total` and `totalPages` then
   * count GROUPS, `itemTotal` the lab work, and each group shows at most
   * `groupItemLimit` of its items.
   */
  meta: PaginatedResponse<Assignment>['meta'] & {
    pageBy?: 'workOrder';
    itemTotal?: number;
    groupItemLimit?: number;
    workOrders?: AssignmentWorkOrderGroup[];
  };
}

/** One choice of the work list's work order filter (D326). */
export interface AssignmentWorkOrderOption {
  id: string;
  number: string;
  clientName: string | null;
  projectName: string | null;
  createdAt: string;
  itemCount: number;
}

export interface AssignmentEvent {
  id: string;
  eventType: string;
  attemptId: string | null;
  fromUser: PersonRef | null;
  toUser: PersonRef | null;
  fromDepartmentId: string | null;
  toDepartmentId: string | null;
  previousExpectedDate: string | null;
  newExpectedDate: string | null;
  reason: string | null;
  note: string | null;
  recordedBy: PersonRef;
  recordedAt: string;
}

export interface AttemptSummary {
  id: string;
  attemptNumber: number;
  isRetest: boolean;
  retestReason: string | null;
  status: string;
  /** Who did the work. A reassignment never changes it. */
  performedBy: PersonRef;
  templateReadinessAtStart: string;
  submittedAt: string | null;
  discontinuedAt: string | null;
  discontinuedReason: string | null;
  createdAt: string;
}

export interface AssignmentDetail extends Assignment {
  events: AssignmentEvent[];
  attempts: AttemptSummary[];
}

/** THE WHOLE GENERAL RESULT. Nothing here is a measurement. */
export interface GeneralResult {
  summaryAr: string | null;
  summaryEn: string | null;
  generalNotes: string | null;
  executionStartedAt: string | null;
  executionCompletedAt: string | null;
}

export interface AttemptEquipmentUse {
  id: string;
  equipmentId: string;
  equipmentCode: string;
  name: string | null;
  usedOn: string;
  statusAtUse: string;
  calibrationRequiredAtUse: boolean;
  calibrationValidUntilAtUse: string | null;
  blockReason: string | null;
  overrideReason: string | null;
  /** The part it played in a specialised template (`balance`, `oven`), or null. */
  role: string | null;
  recordedBy: PersonRef;
  recordedAt: string;
  removedAt: string | null;
  removedBy: PersonRef | null;
  removalReason: string | null;
}

export interface AttemptAttachment {
  id: string;
  fileId: string;
  logicalName: string;
  mimeType: string;
  byteSize: number;
  purpose: string;
  note: string | null;
  attachedBy: PersonRef;
  attachedAt: string;
  removedAt: string | null;
  removedBy: PersonRef | null;
  removalReason: string | null;
}

export interface Attempt {
  id: string;
  assignmentId: string;
  attemptNumber: number;
  isRetest: boolean;
  retestReason: string | null;
  status: string;
  performedBy: PersonRef;
  /** `general`, or the specialised template key it started with (D239). */
  resultPath: ResultPath;
  templateReadinessAtStart: string;
  generalResult: GeneralResult;
  /** The specialised result, server-calculated; null on the general path. */
  structuredResult: StructuredResultView | null;
  /** Read-only facts from the records (D246); null on the general path. */
  header: AttemptHeader | null;
  equipment: AttemptEquipmentUse[];
  attachments: AttemptAttachment[];
  submittedAt: string | null;
  discontinuedAt: string | null;
  discontinuedReason: string | null;
  createdAt: string;
  updatedAt: string;
  assignment: Assignment;
}

export interface Assignee {
  id: string;
  displayName: string;
  role: string;
}

/** Why a work-order service may not be chosen for a sample (D316). */
export type AssignableTestAvailability =
  | 'available'
  | 'not_a_catalogue_test'
  | 'cancelled'
  | 'not_requested_for_sample';

/** One service of the sample's work order, as the assignment form offers it. */
export interface AssignableTest {
  workOrderServiceId: string;
  lineNumber: number;
  catalogueItemId: string | null;
  code: string | null;
  nameAr: string | null;
  nameEn: string | null;
  referenceStandard: string | null;
  standardVersion: string | null;
  serviceState: string;
  templateReadiness: string;
  catalogueItemActive: boolean;
  requestedForSample: boolean;
  availability: AssignableTestAvailability;
  liveAssignments: { id: string; pieceId: string | null; pieceCode: string | null; status: string }[];
}

export interface AssignableTests {
  sampleId: string;
  workOrder: { id: string; number: string | null } | null;
  requestedTestsRecorded: boolean;
  tests: AssignableTest[];
}

type Query = Record<string, string | number | boolean | undefined>;

export const assignmentsApi = {
  list: (query: Query = {}) =>
    apiRequest<AssignmentListResponse>('/assignments', { query }),

  /** The work list's work order filter (D326), searched on the server. */
  workOrderOptions: (query: Query = {}) =>
    apiRequest<{ data: AssignmentWorkOrderOption[] }>('/assignments/work-order-options', { query }),

  /** The caller's own open work. The person comes from the session. */
  myTasks: (query: Query = {}) =>
    apiRequest<AssignmentListResponse>('/assignments/my-tasks', { query }),

  assignees: () => apiRequest<Assignee[]>('/assignments/assignees'),

  /** The sample's work-order tests and their live lab work — never the catalogue (D316). */
  assignableTests: (sampleId: string) =>
    apiRequest<AssignableTests>('/assignments/assignable-tests', { query: { sampleId } }),

  get: (id: string) => apiRequest<AssignmentDetail>(`/assignments/${id}`),

  /** Refused for any sample that is not accepted. A duplicate needs a reason. */
  create: (input: Record<string, unknown>) =>
    apiRequest<AssignmentDetail>('/assignments', { method: 'POST', body: input }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<AssignmentDetail>(`/assignments/${id}`, { method: 'PATCH', body: input }),

  /** Entered work is never reattributed: an open draft is discontinued. */
  setAssignee: (id: string, input: Record<string, unknown>) =>
    apiRequest<AssignmentDetail>(`/assignments/${id}/assignee`, {
      method: 'POST',
      body: input,
    }),

  transferDepartment: (id: string, input: Record<string, unknown>) =>
    apiRequest<AssignmentDetail>(`/assignments/${id}/department`, {
      method: 'POST',
      body: input,
    }),

  recordDelay: (id: string, input: Record<string, unknown>) =>
    apiRequest<AssignmentDetail>(`/assignments/${id}/delays`, {
      method: 'POST',
      body: input,
    }),

  stop: (id: string, input: Record<string, unknown>) =>
    apiRequest<AssignmentDetail>(`/assignments/${id}/stop`, { method: 'POST', body: input }),

  resume: (id: string, input: Record<string, unknown> = {}) =>
    apiRequest<AssignmentDetail>(`/assignments/${id}/resume`, {
      method: 'POST',
      body: input,
    }),

  cancel: (id: string, input: Record<string, unknown>) =>
    apiRequest<AssignmentDetail>(`/assignments/${id}/cancel`, {
      method: 'POST',
      body: input,
    }),

  startAttempt: (id: string, input: Record<string, unknown> = {}) =>
    apiRequest<Attempt>(`/assignments/${id}/attempts`, { method: 'POST', body: input }),
};

export const attemptsApi = {
  get: (id: string) => apiRequest<Attempt>(`/attempts/${id}`),

  /** Saving is not submitting. */
  save: (id: string, input: Partial<GeneralResult>) =>
    apiRequest<Attempt>(`/attempts/${id}`, { method: 'PATCH', body: input }),

  /** Save the specialised result (D2216, D2726, D2041, T85, T96). The server calculates. */
  saveStructured: (id: string, input: SaveStructuredResultInput) =>
    apiRequest<Attempt>(`/attempts/${id}/structured-result`, { method: 'PUT', body: input }),

  /**
   * Submitting for review is not approval. A specialised attempt sends its
   * CURRENT form: the API saves, validates and submits it in one transaction.
   */
  submit: (id: string, structuredResult?: SaveStructuredResultInput | null) =>
    apiRequest<Attempt>(`/attempts/${id}/submit`, {
      method: 'POST',
      body: structuredResult ? { structuredResult } : {},
    }),

  recordEquipment: (id: string, input: Record<string, unknown>) =>
    apiRequest<Attempt>(`/attempts/${id}/equipment`, { method: 'POST', body: input }),

  removeEquipment: (id: string, usageId: string, input: Record<string, unknown> = {}) =>
    apiRequest<Attempt>(`/attempts/${id}/equipment/${usageId}/remove`, {
      method: 'POST',
      body: input,
    }),

  /** Attaching is not reading: nothing inside the file is used. */
  addAttachment: (id: string, input: { file: File; purpose: string; note?: string }) => {
    const form = new FormData();
    form.append('file', input.file, input.file.name);
    form.append('purpose', input.purpose);
    if (input.note) form.append('note', input.note);
    return apiRequest<Attempt>(`/attempts/${id}/attachments`, {
      method: 'POST',
      formData: form,
    });
  },

  downloadAttachment: (id: string, attachmentId: string) =>
    apiDownload(`/attempts/${id}/attachments/${attachmentId}/content`),

  removeAttachment: (id: string, attachmentId: string, reason: string) =>
    apiRequest<Attempt>(`/attempts/${id}/attachments/${attachmentId}/remove`, {
      method: 'POST',
      body: { reason },
    }),
};

/**
 * The equipment shapes are the shared VIEW types (`EquipmentView` and
 * friends, Phase 11 D156) — never restated here. A page imports them from
 * `@lims/shared-types` directly.
 */
export const equipmentApi = {
  list: (query: Query = {}) =>
    apiRequest<PaginatedResponse<EquipmentView>>('/equipment', { query }),

  get: (id: string) => apiRequest<EquipmentView>(`/equipment/${id}`),

  /** Only an instrument nothing ever cited; otherwise 409 `equipment_in_use` (D329). */
  remove: (id: string) => apiRequest<void>(`/equipment/${id}`, { method: 'DELETE' }),

  create: (input: Record<string, unknown>) =>
    apiRequest<EquipmentView>('/equipment', { method: 'POST', body: input }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<EquipmentView>(`/equipment/${id}`, { method: 'PATCH', body: input }),

  setStatus: (id: string, input: Record<string, unknown>) =>
    apiRequest<EquipmentView>(`/equipment/${id}/status`, { method: 'POST', body: input }),

  /** The Phase 8 act. Now ALSO writes a `date_only` history row (D156); a
   *  retired instrument refuses with `equipment_retired`. */
  setCalibration: (id: string, input: Record<string, unknown>) =>
    apiRequest<EquipmentView>(`/equipment/${id}/calibration`, {
      method: 'PUT',
      body: input,
    }),

  // -------------------------------------------------------------------------
  // Calibration history, certificates and usage — Phase 11 (D156)
  // -------------------------------------------------------------------------

  /** Newest first. The append-only history; nothing here is ever edited or
   *  removed, only added to. */
  calibrationHistory: (id: string) =>
    apiRequest<EquipmentCalibrationView[]>(`/equipment/${id}/calibrations`),

  /** Records a calibration. No pass/fail, tolerance or measured value:
   *  whether it is acceptable is the provider's statement on the certificate
   *  (CLAUDE.md section 4). Refused for a retired instrument. */
  recordCalibration: (id: string, input: Record<string, unknown>) =>
    apiRequest<{ equipment: EquipmentView; calibration: EquipmentCalibrationView }>(
      `/equipment/${id}/calibrations`,
      { method: 'POST', body: input },
    ),

  /** Attaching a certificate is not the system reading or verifying it
   *  (CLAUDE.md section 8). Allowed even for a retired or suspended
   *  instrument: evidence may arrive later. */
  uploadCertificate: (
    id: string,
    calibrationId: string,
    input: { file: File; note?: string },
  ) => {
    const form = new FormData();
    form.append('file', input.file, input.file.name);
    if (input.note) form.append('note', input.note);
    return apiRequest<EquipmentCalibrationView>(
      `/equipment/${id}/calibrations/${calibrationId}/certificates`,
      { method: 'POST', formData: form },
    );
  },

  /** A removed certificate still downloads — the link was withdrawn, the
   *  evidence kept. */
  downloadCertificate: (id: string, calibrationId: string, certificateId: string) =>
    apiDownload(
      `/equipment/${id}/calibrations/${calibrationId}/certificates/${certificateId}/content`,
    ),

  /** Marks the link removed, with a reason. The stored file is NOT deleted. */
  removeCertificate: (
    id: string,
    calibrationId: string,
    certificateId: string,
    reason: string,
  ) =>
    apiRequest<EquipmentCalibrationView>(
      `/equipment/${id}/calibrations/${calibrationId}/certificates/${certificateId}/remove`,
      { method: 'POST', body: { reason } },
    ),

  /** Where the instrument was used, newest first — read-only, from the
   *  attempt records. */
  usage: (id: string, query: { page?: number; pageSize?: number } = {}) =>
    apiRequest<PaginatedResponse<EquipmentUsageView>>(`/equipment/${id}/usage`, {
      query: { page: query.page ?? 1, pageSize: query.pageSize ?? 25 },
    }),
};

export interface Specification {
  id: string;
  code: string;
  nameAr: string | null;
  nameEn: string | null;
  issuingBody: string | null;
  notes: string | null;
  isActive: boolean;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Empty until a laboratory fills it in. The product seeds no standard. */
export const specificationsApi = {
  list: (query: Query = {}) =>
    apiRequest<PaginatedResponse<Specification>>('/test-specifications', { query }),

  create: (input: Record<string, unknown>) =>
    apiRequest<Specification>('/test-specifications', { method: 'POST', body: input }),

  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<Specification>(`/test-specifications/${id}`, {
      method: 'PATCH',
      body: input,
    }),
};

/** A section the caller may not see is `null`, never zero. */
export interface DashboardSummary {
  asOf: string;
  samples: { awaitingInspection: number; held: number } | null;
  work: {
    unassigned: number;
    inProgress: number;
    delayed: number;
    stopped: number;
    awaitingReview: number;
  } | null;
  myTasks: { open: number; delayed: number } | null;
  equipment: { inService: number; blockedToday: number } | null;
  /**
   * Phase 9. Each is `null` — never 0 — for a caller without the matching
   * permission, so an absent figure never reads as "nothing is waiting".
   */
  awaitingReview: number | null;
  awaitingApproval: number | null;
  returnedToMe: number | null;
}

export const dashboardApi = {
  summary: () => apiRequest<DashboardSummary>('/dashboard/summary'),
  /** Phase 12 (D174). Each section is `null` without its permission; the
   *  finance section exists only for `finance.view`, decided by the server. */
  management: () => apiRequest<ManagementDashboardView>('/dashboard/management'),
};

// ---------------------------------------------------------------------------
// Phase 9 — technical review, final approval and post-approval corrections
//
// ===========================================================================
// READ CLAUDE.md SECTIONS 4 AND 8 BEFORE ADDING A FIELD BELOW.
// ===========================================================================
//
// A review records that a RECORD was checked, and an approval records that a
// RECORD was checked by a second person. NEITHER SAYS THE MATERIAL PASSED.
// There is no conformity field, no pass, no fail, no limit and no verdict in
// any shape here, for any test — that is test science, and it is gated on a
// validated worked example.
// ---------------------------------------------------------------------------

export interface ReviewActionView {
  id: string;
  decision: ReviewDecision;
  /** The submission this person actually read. Never "whatever is current". */
  revisionNumber: number;
  reason: string | null;
  note: string | null;
  actedBy: PersonRef;
  actedAt: string;
}

export interface ApprovalActionView {
  id: string;
  decision: ApprovalDecision;
  revisionNumber: number;
  reason: string | null;
  note: string | null;
  actedBy: PersonRef;
  actedAt: string;
}

/** One frozen submission. Snapshotted by the database, never rewritten. */
export interface ResultRevisionView {
  id: string;
  revisionNumber: number;
  submittedAt: string;
  submittedBy: PersonRef;
  content: GeneralResultContent;
  equipment: RevisionEquipmentEntry[];
  attachments: RevisionAttachmentEntry[];
  /** The specialised template it was submitted on, or null (D239). */
  resultTemplate: string | null;
  /** The frozen specialised result: readings and server values (D240). */
  structured: StructuredDocument | null;
}

/** A lock is never deleted, only released — and a release names its reason. */
export interface ResultLockView {
  id: string;
  lockedAt: string;
  lockedBy: PersonRef;
  revisionNumber: number;
  releasedAt: string | null;
  releasedBy: PersonRef | null;
  correctionRequestId: string | null;
  isActive: boolean;
}

export interface CorrectionRequestView {
  id: string;
  assignmentId: string;
  attemptId: string;
  status: CorrectionStatus;
  reason: string;
  summary: string | null;
  openedBy: PersonRef;
  openedAt: string;
  resolvedAt: string | null;
  resolvedBy: PersonRef | null;
}

/**
 * The whole recorded life of one attempt.
 *
 * `comparison` is computed by the BACKEND with the shared `compareRevisions`.
 * This application renders it and never re-derives it: a second implementation
 * of the same diff could disagree with the one the server recorded, and then
 * two people would be looking at different accounts of the same change.
 *
 * `canReview` / `canApprove` / `canCorrect` are presentation hints that already
 * combine permission, state and "not your own work". They decide what to SHOW.
 * Every endpoint re-checks all of it (CLAUDE.md section 5.1).
 */
export interface ResultHistoryView {
  attemptId: string;
  assignmentId: string;
  attemptStatus: AttemptStatus;
  performedBy: PersonRef;
  /** Null before anything was ever submitted. Not zero. */
  latestRevisionNumber: number | null;
  revisions: ResultRevisionView[];
  reviewActions: ReviewActionView[];
  approvalActions: ApprovalActionView[];
  corrections: CorrectionRequestView[];
  lock: ResultLockView | null;
  comparison: RevisionComparison | null;
  callerIsPerformer: boolean;
  canReview: boolean;
  canApprove: boolean;
  canCorrect: boolean;
}

export interface ReviewQueueItemView {
  attemptId: string;
  assignmentId: string;
  attemptNumber: number;
  isRetest: boolean;
  retestReason: string | null;
  revisionNumber: number;
  submittedAt: string;
  performedBy: PersonRef;
  /** The caller's own work is SHOWN and flagged, with the action refused. */
  callerIsPerformer: boolean;
  returnCount: number;
  assignment: Assignment;
}

export interface ApprovalQueueItemView extends ReviewQueueItemView {
  technicallyAcceptedAt: string;
  technicallyAcceptedBy: PersonRef;
  reviewNote: string | null;
}

export interface ReturnedWorkItemView {
  attemptId: string;
  assignmentId: string;
  attemptNumber: number;
  revisionNumber: number;
  returnedAt: string;
  returnedBy: PersonRef;
  /** Which stage sent it back. Review and approval are different acts. */
  returnStage: 'review' | 'approval';
  /** The reviewer's or approver's own words. Shown verbatim, never summarised. */
  reason: string;
  assignment: Assignment;
}

/**
 * Technical review (module C7).
 *
 * Every act names the `revisionNumber` the person actually read. If the result
 * was returned and resubmitted in the meantime the server answers `409
 * CONFLICT` rather than recording an acceptance of text nobody saw.
 */
export const reviewApi = {
  queue: (query: Query = {}) =>
    apiRequest<PaginatedResponse<ReviewQueueItemView>>('/review/queue', { query }),

  /** Technical acceptance. NOT an approval, and it locks nothing. */
  accept: (attemptId: string, input: { revisionNumber: number; note?: string }) =>
    apiRequest<Attempt>(`/review/attempts/${attemptId}/accept`, {
      method: 'POST',
      body: input,
    }),

  /** Back to its performer to correct the record. The reason is mandatory. */
  return: (attemptId: string, input: { revisionNumber: number; reason: string }) =>
    apiRequest<Attempt>(`/review/attempts/${attemptId}/return`, {
      method: 'POST',
      body: input,
    }),

  /** The WORK must be performed again. A different act from returning it. */
  requireRetest: (attemptId: string, input: { revisionNumber: number; reason: string }) =>
    apiRequest<Attempt>(`/review/attempts/${attemptId}/require-retest`, {
      method: 'POST',
      body: input,
    }),
};

/** Final approval (module C8). A separate permission and a separate record. */
export const approvalApi = {
  queue: (query: Query = {}) =>
    apiRequest<PaginatedResponse<ApprovalQueueItemView>>('/approval/queue', { query }),

  /** Records that the RECORD was checked. It states nothing about the material. */
  approve: (attemptId: string, input: { revisionNumber: number; note?: string }) =>
    apiRequest<Attempt>(`/approval/attempts/${attemptId}/approve`, {
      method: 'POST',
      body: input,
    }),

  return: (attemptId: string, input: { revisionNumber: number; reason: string }) =>
    apiRequest<Attempt>(`/approval/attempts/${attemptId}/return`, {
      method: 'POST',
      body: input,
    }),
};

/** The caller's own returned work. The person comes from the session. */
export const returnedWorkApi = {
  list: (query: Query = {}) =>
    apiRequest<PaginatedResponse<ReturnedWorkItemView>>('/results/returned', { query }),
};

export const resultHistoryApi = {
  get: (attemptId: string) =>
    apiRequest<ResultHistoryView>(`/attempts/${attemptId}/history`),

  /**
   * Open a post-approval correction.
   *
   * A recorded PROCEDURE, not an edit: it releases the lock, sends the result
   * back to its performer and requires review and approval again. It cannot be
   * withdrawn, and it resolves only on re-approval (D102).
   */
  openCorrection: (attemptId: string, input: { reason: string; summary?: string }) =>
    apiRequest<Attempt>(`/attempts/${attemptId}/corrections`, {
      method: 'POST',
      body: input,
    }),
};

// ---------------------------------------------------------------------------
// Phase 10 — report templates (sub-phase 10a, contract section 5.1)
//
// ===========================================================================
// READ CLAUDE.md SECTIONS 4 AND 6.1 BEFORE ADDING A FIELD BELOW.
// ===========================================================================
//
// ONE layout exists, `general` (D112). A template names which laboratory
// register entry a report is issued on and in which languages; it carries no
// table, chart, calculation, limit or unit, because every one of those is test
// science. No template is seeded: the register starts empty and the
// laboratory's manager creates the first one.
// ---------------------------------------------------------------------------

export type { ReportTemplateView };

/**
 * The detail code the API sends, with `SERVICE_UNAVAILABLE`, when this
 * installation cannot render a PDF at all. The interface must say so plainly
 * and never show anything that looks like a preview in its place.
 */
export const REPORT_RENDERING_UNAVAILABLE = 'report_rendering_unavailable';

/** True for the API's "rendering is unavailable here" refusal. */
export function isRenderingUnavailable(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.details.some(
      (detail) =>
        detail.code === REPORT_RENDERING_UNAVAILABLE ||
        detail.message === REPORT_RENDERING_UNAVAILABLE,
    )
  );
}

/**
 * POST a JSON body to an authenticated route that answers with a FILE.
 *
 * `apiDownload` covers GET; a preview is a POST because it names the language
 * to render in. The bearer token travels in a header exactly as for any other
 * call, and nothing is stored anywhere by asking.
 *
 * On a 401 the shared client is asked to recover the session through one
 * ordinary authenticated call — so the refresh stays the client's single,
 * deduplicated one (a second rotation would look like a replay) and a session
 * that is really gone signs the user out the usual way — then the POST is
 * retried once.
 */
export async function apiPostForBlob(
  path: string,
  body: unknown,
  retrying = false,
): Promise<Blob> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = session.accessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError('NETWORK_ERROR', 0, 'The server could not be reached.');
  }

  if (response.status === 401 && !retrying) {
    // Throws — and signs the user out — when the session cannot be recovered.
    await apiRequest<unknown>('/auth/me');
    return apiPostForBlob(path, body, true);
  }

  if (!response.ok) {
    let payload: ApiErrorBody | null = null;
    try {
      payload = (await response.json()) as ApiErrorBody;
    } catch {
      payload = null;
    }
    throw new ApiError(
      payload?.error?.code ?? ERROR_CODES.INTERNAL_ERROR,
      response.status,
      payload?.error?.message ?? 'The request failed.',
      payload?.error?.details ?? [],
      payload?.error?.requestId,
    );
  }

  return response.blob();
}

export const reportTemplatesApi = {
  /** Needs `templates.view`. `status` absent lists every template. */
  list: (query: {
    page?: number;
    pageSize?: number;
    status?: ReportTemplateStatus;
  } = {}) =>
    apiRequest<PaginatedResponse<ReportTemplateView>>('/report-templates', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.status ? { status: query.status } : {}),
      },
    }),

  get: (id: string) =>
    apiRequest<ReportTemplateView>(`/report-templates/${id}`),

  /** Needs `templates.manage`. The layout is always `general`; it is not sent. */
  create: (input: CreateReportTemplateInput) =>
    apiRequest<ReportTemplateView>('/report-templates', {
      method: 'POST',
      body: input,
    }),

  /** Active templates only. Every edit raises the revision by one. */
  update: (id: string, input: UpdateReportTemplateInput) =>
    apiRequest<ReportTemplateView>(`/report-templates/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  /** Active templates only. The previous default is cleared in the same transaction. */
  setDefault: (id: string) =>
    apiRequest<ReportTemplateView>(`/report-templates/${id}/default`, {
      method: 'POST',
    }),

  /**
   * Withdraw from use. TERMINAL, the reason is mandatory, and no report
   * already issued on the template is altered.
   */
  retire: (id: string, input: { reason: string }) =>
    apiRequest<ReportTemplateView>(`/report-templates/${id}/retire`, {
      method: 'POST',
      body: input,
    }),

  /**
   * A SAMPLE rendering, produced on request and never stored (D127): the
   * laboratory's real header, a body of placeholder text, "SAMPLE" on every
   * page, no report number and no QR. Resolves to the PDF bytes.
   */
  preview: (
    id: string,
    input: {
      language: ReportLanguage;
      /** `result` prints one approved result (needs reports.prepare or reports.issue). */
      source?: 'sample' | 'result';
      attemptId?: string;
      /** For a sample: `general` or an implemented result-template key. */
      sampleResultTemplate?: string;
    },
  ) => apiPostForBlob(`/report-templates/${id}/preview`, input),

  /**
   * Which template a report on these results would use when none is chosen
   * (D254): the tests' own, otherwise the laboratory default.
   */
  resolution: (attemptIds: readonly string[]) =>
    apiRequest<ReportTemplateResolutionView>('/report-templates/resolution', {
      query: { attemptIds: attemptIds.join(',') },
    }),
};

// ---------------------------------------------------------------------------
// Phase 10 — reports up to and including issuance (sub-phase 10b, contract
// sections 5.2 and 5.4)
//
// ===========================================================================
// ISSUANCE IS NOT DELIVERY. A STAFF DOWNLOAD IS NOT A DELIVERY EITHER.
// ===========================================================================
//
// Nothing below sends a report to anybody. Issuing renders the PDF ONCE and
// stores it; every later download serves those stored bytes (D116, D129). No
// function here takes or returns a storage key, a path or a file id: a version's
// file is addressed by the report and its version number, and nothing else.
// ---------------------------------------------------------------------------

export type { EligibleResultView, ReportDetailView, ReportRegisterItemView };

/**
 * What `GET /reports/eligible-results` says beside the rows: the work order, and
 * the laboratory's own `allowPartialReports` rule — returned there so the page
 * can state it without the preparer holding `settings.view`.
 */
export interface EligibleResultsMeta {
  readonly workOrder: { readonly id: string; readonly number: string };
  readonly allowPartialReports: boolean;
}

/** The register's filters (`reportRegisterQuerySchema`). `search` matches the report number. */
export interface ReportRegisterQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: ReportStatus;
  readonly deliveryStatus?: ReportDeliveryStatus;
  readonly clientId?: string;
  readonly projectId?: string;
  readonly workOrderId?: string;
  readonly search?: string;
  /** ISO calendar dates, `YYYY-MM-DD`. */
  readonly issuedFrom?: string;
  readonly issuedTo?: string;
}

export const reportsApi = {
  /** Needs `reports.view`. */
  list: (query: ReportRegisterQuery = {}) =>
    apiRequest<PaginatedResponse<ReportRegisterItemView>>('/reports', {
      query: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 25,
        ...(query.status ? { status: query.status } : {}),
        ...(query.deliveryStatus ? { deliveryStatus: query.deliveryStatus } : {}),
        ...(query.clientId ? { clientId: query.clientId } : {}),
        ...(query.projectId ? { projectId: query.projectId } : {}),
        ...(query.workOrderId ? { workOrderId: query.workOrderId } : {}),
        ...(query.search ? { search: query.search } : {}),
        ...(query.issuedFrom ? { issuedFrom: query.issuedFrom } : {}),
        ...(query.issuedTo ? { issuedTo: query.issuedTo } : {}),
      },
    }),

  /**
   * Needs `reports.prepare`. The approved, locked results of ONE work order,
   * each saying whether it is already in another live report. `meta` carries
   * the laboratory's own partial-report rule, so a preparer who may not read
   * the settings (a supervisor) is still told it before the server refuses.
   */
  eligibleResults: (workOrderId: string) =>
    apiRequest<{ data: EligibleResultView[]; meta: EligibleResultsMeta }>(
      '/reports/eligible-results',
      { query: { workOrderId } },
    ),

  /** Needs `reports.prepare`. Creates the report and its draft version 1. No number. */
  prepare: (input: PrepareReportInput) =>
    apiRequest<ReportDetailView>('/reports', { method: 'POST', body: input }),

  /**
   * Needs `reports.prepare` AND `reports.issue` (D330). Prepares and issues in
   * ONE transaction through the same rules as `prepare` + `issue`: any refusal
   * leaves no report, no draft and no number. Sends nothing to anyone.
   */
  prepareAndIssue: (input: PrepareReportInput) =>
    apiRequest<ReportDetailView>('/reports/issue', { method: 'POST', body: input }),

  /** Needs `reports.view`. */
  get: (id: string) => apiRequest<ReportDetailView>(`/reports/${id}`),

  /** Needs `reports.prepare`. `draftRevision` is the revision the editor had in front of them. */
  updateDraft: (id: string, input: UpdateReportDraftInput) =>
    apiRequest<ReportDetailView>(`/reports/${id}/draft`, {
      method: 'PATCH',
      body: input,
    }),

  /**
   * Needs `reports.prepare` or `reports.issue`. A PREVIEW rendered now and
   * never stored (D127): marked on every page, no number, no QR. PDF bytes.
   */
  previewDraft: (id: string) => apiPostForBlob(`/reports/${id}/draft/preview`, {}),

  /**
   * Needs `reports.issue`. Echoes the draft revision the issuer read; a changed
   * draft is refused (`draft_changed`). Sends nothing to anyone.
   */
  issue: (id: string, input: { draftRevision: number }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/draft/issue`, {
      method: 'POST',
      body: input,
    }),

  /** Needs `reports.prepare`. Only a report never issued. Kept as `discarded`, never deleted. */
  discard: (id: string, input: { reason: string }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/draft/discard`, {
      method: 'POST',
      body: input,
    }),

  /**
   * Needs `reports.view`. The STORED bytes of one issued version, re-checked
   * against the recorded fingerprint. Recorded as a download — never as a
   * delivery or a client's receipt (D129).
   */
  downloadVersionFile: (id: string, versionNumber: number, language: ReportLanguage) =>
    // The language is ALWAYS named: the person chose it, never the interface (D330).
    apiDownload(`/reports/${id}/versions/${versionNumber}/file?language=${language}`),

  // -------------------------------------------------------------------------
  // Sub-phase 10c — acts on a report after issuance (contract section 5.3).
  //
  // None of these edits an issued document, and none accepts a status, a
  // number, a date of issue or a verdict. Each is an ACT the server re-checks
  // against the report's state, records with a status event and an audit entry,
  // and answers with the whole `ReportDetailView`.
  // -------------------------------------------------------------------------

  /**
   * Needs `reports.correct`. Opens a correction — the replacement draft,
   * version N+1. The current version stays in force until the replacement is
   * issued. `affectedAttemptIds` only with a `scientific` impact.
   */
  openCorrection: (id: string, input: OpenReportCorrectionInput) =>
    apiRequest<ReportDetailView>(`/reports/${id}/corrections`, {
      method: 'POST',
      body: input,
    }),

  /** Needs `reports.suspend`. `issued_valid → suspended`, with the reason. */
  suspend: (id: string, input: { reason: string }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/suspend`, {
      method: 'POST',
      body: input,
    }),

  /** Needs `reports.suspend`. `suspended → issued_valid`; the reason is the documented final decision. */
  reinstate: (id: string, input: { reason: string }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/reinstate`, {
      method: 'POST',
      body: input,
    }),

  /**
   * Needs `reports.cancel`. Terminal. The number, files and history are kept
   * and the number is never reused; a pending replacement draft is discarded
   * with the same reason.
   */
  cancel: (id: string, input: { reason: string }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/cancel`, {
      method: 'POST',
      body: input,
    }),

  /**
   * Needs `reports.deliver`. RECORDS what a person did outside the system — a
   * delivery or a notice. The system sends nothing (`sentBySystem` is always
   * false) and no status changes.
   */
  recordDelivery: (id: string, input: RecordReportDeliveryInput) =>
    apiRequest<ReportDetailView>(`/reports/${id}/deliveries`, {
      method: 'POST',
      body: input,
    }),

  /** Needs `payments.record`. `held → released`, once, with a note. Changes access only. */
  releasePaymentHold: (id: string, input: { note: string }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/payment-hold/release`, {
      method: 'POST',
      body: input,
    }),

  // -------------------------------------------------------------------------
  // The client portal — Phase 11 (D149). Releasing is not delivery, and
  // withdrawing changes nothing already downloaded.
  // -------------------------------------------------------------------------

  /** Needs `reports.deliver`. Releases the CURRENT version. */
  portalRelease: (id: string, input: { note?: string }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/portal-release`, {
      method: 'POST',
      body: input,
    }),

  /** Needs `reports.deliver`. Withdraws the newest version currently in the portal. */
  withdrawPortalRelease: (id: string, input: { reason: string }) =>
    apiRequest<ReportDetailView>(`/reports/${id}/portal-release/withdraw`, {
      method: 'POST',
      body: input,
    }),
};

// ---------------------------------------------------------------------------
// Phase 10 — PUBLIC report verification (contract section 6, D122, D123)
//
// ===========================================================================
// THE ONLY CALL IN THIS FILE MADE ON BEHALF OF SOMEBODY WHO IS NOT SIGNED IN.
// ===========================================================================
//
// Whoever holds a printed report holds its link, so this client:
//  - sends NO Authorization header, even when a session exists in this
//    browser — a signed-in member of staff sees exactly what a stranger sees;
//  - never asks for a session refresh and never signs anybody out, whatever
//    the status: a 401 or 404 here says nothing about a session;
//  - copies ONLY the keys of `PUBLIC_VERIFICATION_FIELDS` out of the body, so a
//    key the API should never have sent cannot reach a screen through here;
//  - never produces `unavailable` as a claim about the report: it means this
//    page could not get an answer (a network failure, any 5xx, a 429, or a
//    body it could not read), and the page says that does not make the report
//    invalid.
// ---------------------------------------------------------------------------

/** Why no answer was obtained. Never a statement about the report. */
export type VerificationUnavailableReason =
  | 'network'
  | 'server'
  | 'rate_limited'
  | 'unexpected';

export type PublicVerificationResult =
  | { readonly kind: 'found'; readonly view: PublicVerificationView }
  | { readonly kind: 'not_found' }
  | {
      readonly kind: 'unavailable';
      readonly reason: VerificationUnavailableReason;
      /** From `Retry-After`, in whole seconds from now; null when absent or unreadable. */
      readonly retryAfterSeconds: number | null;
    };

/**
 * `Retry-After` as seconds from now. The header may be a number of seconds or
 * an HTTP date; anything else is ignored rather than guessed at.
 */
export function parseRetryAfter(
  value: string | null,
  now: number = Date.now(),
): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

const VERIFIED_STATUSES: readonly PublicVerificationView['status'][] = [
  'issued_valid',
  'superseded',
  'cancelled',
  'suspended',
];

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === 'string';

interface BilingualValue {
  readonly ar: string | null;
  readonly en: string | null;
}

function readBilingual(value: unknown): BilingualValue | null {
  if (!isRecord(value)) return null;
  const { ar, en } = value;
  if (!isNullableString(ar) || !isNullableString(en)) return null;
  // An absent language is null, never an empty string standing in for one.
  const normalise = (text: string | null) =>
    text === null || text.trim() === '' ? null : text;
  return { ar: normalise(ar), en: normalise(en) };
}

/**
 * Build the view from the allow-list, and nothing else.
 *
 * Returns null when a listed key is missing or malformed: a page that cannot
 * read the answer says it could not verify, rather than filling a gap with a
 * value nobody sent (an empty field is never zero).
 */
export function readPublicVerificationView(
  body: unknown,
): PublicVerificationView | null {
  if (!isRecord(body)) return null;
  const source: UnknownRecord = {};
  for (const key of PUBLIC_VERIFICATION_FIELDS) source[key] = body[key];

  const status = source.status as PublicVerificationView['status'];
  if (!VERIFIED_STATUSES.includes(status)) return null;

  const laboratoryName = readBilingual(source.laboratoryName);
  if (!laboratoryName) return null;

  const { reportNumber, versionNumber, issuedAt, pageCount, originalAvailable } = source;
  if (typeof reportNumber !== 'string' || reportNumber.trim() === '') return null;
  if (typeof versionNumber !== 'number' || !Number.isInteger(versionNumber)) return null;
  if (typeof issuedAt !== 'string' || Number.isNaN(Date.parse(issuedAt))) return null;
  if (typeof pageCount !== 'number' || !Number.isInteger(pageCount)) return null;
  if (typeof originalAvailable !== 'boolean') return null;

  const language = source.language as PublicVerificationView['language'];
  if (!ALL_REPORT_LANGUAGES.includes(language)) return null;

  if (!Array.isArray(source.testTitles)) return null;
  const testTitles: BilingualValue[] = [];
  for (const entry of source.testTitles) {
    const title = readBilingual(entry);
    if (!title) return null;
    testTitles.push(title);
  }

  let latestVersion: PublicVerificationView['latestVersion'] = null;
  if (source.latestVersion !== null && source.latestVersion !== undefined) {
    if (!isRecord(source.latestVersion)) return null;
    const latestNumber = source.latestVersion.versionNumber;
    const latestStatus = source.latestVersion.status as ReportVersionStatus;
    if (typeof latestNumber !== 'number' || !Number.isInteger(latestNumber)) return null;
    if (!ALL_REPORT_VERSION_STATUSES.includes(latestStatus)) return null;
    latestVersion = { versionNumber: latestNumber, status: latestStatus };
  }

  return {
    status,
    laboratoryName,
    reportNumber,
    versionNumber,
    testTitles,
    issuedAt,
    pageCount,
    language,
    latestVersion,
    originalAvailable,
  };
}

export const publicVerificationApi = {
  /**
   * `GET /verify/{token}`. A token that cannot be one is answered `not_found`
   * WITHOUT a request — the API would give the same 404, and there is no
   * reason to send a malformed string anywhere.
   */
  get: async (token: string): Promise<PublicVerificationResult> => {
    if (!isReportVerificationToken(token)) return { kind: 'not_found' };

    let response: Response;
    try {
      response = await fetch(apiUrl(`/verify/${encodeURIComponent(token)}`), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      });
    } catch {
      return { kind: 'unavailable', reason: 'network', retryAfterSeconds: null };
    }

    if (response.status === 404) return { kind: 'not_found' };

    if (response.status === 429 || response.status >= 500) {
      return {
        kind: 'unavailable',
        reason: response.status === 429 ? 'rate_limited' : 'server',
        retryAfterSeconds: parseRetryAfter(response.headers.get('Retry-After')),
      };
    }

    // Anything else the contract does not name is not an answer about the
    // report — including a 401 or 403, which must never touch a session here.
    if (response.status !== 200) {
      return { kind: 'unavailable', reason: 'unexpected', retryAfterSeconds: null };
    }

    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const view = readPublicVerificationView(body);
    return view
      ? { kind: 'found', view }
      : { kind: 'unavailable', reason: 'unexpected', retryAfterSeconds: null };
  },

  /**
   * `GET /verify/{token}/download`, fetched as bytes the page saves itself.
   *
   * Fetched rather than followed as a link, so that a refusal becomes a
   * translated sentence on the page instead of the API's JSON in a bare tab.
   * The same rules as `get`: no Authorization, no session, no refresh. Never a
   * storage path or a file id; the API decides what, if anything, it serves
   * (a superseded token resolves to the latest valid version and names it in
   * `X-Report-Version-Served`).
   *
   * A refusal carries its code and nothing else. `ORIGINAL_NOT_AVAILABLE` is
   * ONE answer for "this laboratory does not offer downloads" and "access is
   * held" (D111, D115), and this client never tries to tell them apart. A 200
   * that is not a PDF is not the document, whatever answered.
   */
  download: async (token: string): Promise<PublicVerificationDownloadResult> => {
    if (!isReportVerificationToken(token)) return { kind: 'refused', refusal: 'NOT_FOUND' };

    let response: Response;
    try {
      response = await fetch(apiUrl(`/verify/${encodeURIComponent(token)}/download`), {
        method: 'GET',
        headers: { Accept: 'application/pdf' },
        credentials: 'omit',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      });
    } catch {
      return { kind: 'unavailable', reason: 'network', retryAfterSeconds: null };
    }

    if (response.status === 429 || response.status >= 500) {
      return {
        kind: 'unavailable',
        reason: response.status === 429 ? 'rate_limited' : 'server',
        retryAfterSeconds: parseRetryAfter(response.headers.get('Retry-After')),
      };
    }

    if (response.status === 200) {
      const contentType = (response.headers.get('Content-Type') ?? '').toLowerCase();
      if (!contentType.startsWith('application/pdf')) {
        return { kind: 'unavailable', reason: 'unexpected', retryAfterSeconds: null };
      }
      let blob: Blob;
      try {
        blob = await response.blob();
      } catch {
        return { kind: 'unavailable', reason: 'network', retryAfterSeconds: null };
      }
      return {
        kind: 'file',
        blob,
        servedVersionNumber: readServedVersionNumber(
          response.headers.get('X-Report-Version-Served'),
        ),
      };
    }

    const refusal = downloadRefusalFor(response.status, await readErrorCode(response));
    return refusal
      ? { kind: 'refused', refusal }
      : { kind: 'unavailable', reason: 'unexpected', retryAfterSeconds: null };
  },
};

/** What the public download route can refuse with. The code is the whole answer. */
export type VerificationDownloadRefusal =
  | 'ORIGINAL_NOT_AVAILABLE'
  | 'REPORT_CANCELLED'
  | 'REPORT_SUSPENDED'
  | 'NOT_FOUND';

export type PublicVerificationDownloadResult =
  | {
      readonly kind: 'file';
      readonly blob: Blob;
      /** From `X-Report-Version-Served`; null when absent or unreadable. */
      readonly servedVersionNumber: number | null;
    }
  | { readonly kind: 'refused'; readonly refusal: VerificationDownloadRefusal }
  | {
      readonly kind: 'unavailable';
      readonly reason: VerificationUnavailableReason;
      readonly retryAfterSeconds: number | null;
    };

function readServedVersionNumber(value: string | null): number | null {
  const trimmed = value?.trim() ?? '';
  if (!/^\d+$/.test(trimmed)) return null;
  const versionNumber = Number(trimmed);
  return Number.isSafeInteger(versionNumber) && versionNumber > 0 ? versionNumber : null;
}

async function readErrorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.json()) as ApiErrorBody | null;
    const code = body?.error?.code;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  }
}

/**
 * A refusal the page can put into words, or null for anything the contract
 * does not name (which the page reports as "could not be obtained").
 *
 * EVERY 403 is "not available from this page", whatever code it carries: the
 * page must never be able to say more than that about why.
 */
function downloadRefusalFor(
  status: number,
  code: string | null,
): VerificationDownloadRefusal | null {
  if (status === 404) return 'NOT_FOUND';
  if (status === 403) return 'ORIGINAL_NOT_AVAILABLE';
  if (status === 409 && code === ERROR_CODES.REPORT_CANCELLED) return 'REPORT_CANCELLED';
  if (status === 409 && code === ERROR_CODES.REPORT_SUSPENDED) return 'REPORT_SUSPENDED';
  return null;
}

// ---------------------------------------------------------------------------
// Phase 12 — quality, maintenance, notifications and import (D161–D178)
//
// Every act is re-checked by its endpoint; the `can` hints the server sends
// decide only what is offered. Nothing here deletes anything.
// ---------------------------------------------------------------------------

export const qualityApi = {
  list: (query: Query = {}) =>
    apiRequest<PaginatedResponse<QualityRecordView>>('/quality-records', { query }),
  get: (id: string) => apiRequest<QualityRecordDetailView>(`/quality-records/${id}`),
  raise: (input: Record<string, unknown>) =>
    apiRequest<QualityRecordDetailView>('/quality-records', { method: 'POST', body: input }),
  update: (id: string, input: Record<string, unknown>) =>
    apiRequest<QualityRecordDetailView>(`/quality-records/${id}`, { method: 'PATCH', body: input }),
  start: (id: string) =>
    apiRequest<QualityRecordDetailView>(`/quality-records/${id}/start`, { method: 'POST', body: {} }),
  close: (id: string, input: Record<string, unknown>) =>
    apiRequest<QualityRecordDetailView>(`/quality-records/${id}/close`, { method: 'POST', body: input }),
  cancel: (id: string, reason: string) =>
    apiRequest<QualityRecordDetailView>(`/quality-records/${id}/cancel`, { method: 'POST', body: { reason } }),
  addAction: (id: string, input: Record<string, unknown>) =>
    apiRequest<QualityRecordDetailView>(`/quality-records/${id}/corrective-actions`, {
      method: 'POST',
      body: input,
    }),
  listActions: (query: Query = {}) =>
    apiRequest<PaginatedResponse<CorrectiveActionView>>('/corrective-actions', { query }),
  completeAction: (id: string, input: Record<string, unknown>) =>
    apiRequest<CorrectiveActionView>(`/corrective-actions/${id}/complete`, { method: 'POST', body: input }),
  verifyAction: (id: string, input: Record<string, unknown>) =>
    apiRequest<CorrectiveActionView>(`/corrective-actions/${id}/verify`, { method: 'POST', body: input }),
  cancelAction: (id: string, reason: string) =>
    apiRequest<CorrectiveActionView>(`/corrective-actions/${id}/cancel`, { method: 'POST', body: { reason } }),
};

export const maintenanceApi = {
  list: (query: Query = {}) =>
    apiRequest<PaginatedResponse<MaintenanceRecordView>>('/maintenance', { query }),
  get: (id: string) => apiRequest<MaintenanceRecordView>(`/maintenance/${id}`),
  schedule: (input: Record<string, unknown>) =>
    apiRequest<MaintenanceRecordView>('/maintenance/planned', { method: 'POST', body: input }),
  /** Reporting a fault does NOT suspend the instrument (D168). */
  reportFault: (input: Record<string, unknown>) =>
    apiRequest<MaintenanceRecordView>('/maintenance/faults', { method: 'POST', body: input }),
  complete: (id: string, input: Record<string, unknown>) =>
    apiRequest<CompletedMaintenanceView>(`/maintenance/${id}/complete`, { method: 'POST', body: input }),
  resolve: (id: string, input: Record<string, unknown>) =>
    apiRequest<MaintenanceRecordView>(`/maintenance/${id}/resolve`, { method: 'POST', body: input }),
  cancel: (id: string, reason: string) =>
    apiRequest<MaintenanceRecordView>(`/maintenance/${id}/cancel`, { method: 'POST', body: { reason } }),
};

/** The caller's OWN notifications (D171); the person comes from the session. */
export const notificationsApi = {
  list: (query: Query = {}) => apiRequest<NotificationListView>('/notifications', { query }),
  unreadCount: () => apiRequest<{ unread: number }>('/notifications/unread-count'),
  markRead: (id: string) =>
    apiRequest<NotificationView>(`/notifications/${id}/read`, { method: 'POST', body: {} }),
  markAllRead: () =>
    apiRequest<{ marked: number }>('/notifications/read-all', { method: 'POST', body: {} }),
  preferences: () => apiRequest<NotificationPreferencesView>('/notifications/preferences'),
  updatePreferences: (preferences: readonly NotificationPreferenceView[]) =>
    apiRequest<NotificationPreferencesView>('/notifications/preferences', {
      method: 'PUT',
      body: { preferences },
    }),
};

/** Validate writes nothing; commit writes every row or none (D175). */
export const importsApi = {
  validate: (kind: ImportKind, csv: string) =>
    apiRequest<ImportReport>(`/imports/${kind}/validate`, { method: 'POST', body: { csv } }),
  commit: (kind: ImportKind, csv: string) =>
    apiRequest<ImportReport>(`/imports/${kind}/commit`, { method: 'POST', body: { csv } }),
};
